import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import { getEncryptedLeadId } from '../../utils/routeEncryption';
import {
  UserPlus,
  Phone,
  User,
  MapPin,
  Activity,
  CreditCard,
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Search,
  Check,
  ClipboardList,
  Eye,
  ShieldCheck,
  Send,
  Mail,
  MessageSquare,
  Share2,
  Stethoscope,
  Building2,
  Building,
  Calendar,
  CalendarClock,
  Sparkles,
  RotateCcw,
  Users,
  ChevronDown,
  HeartPulse,
  Droplet,
  Pill,
  AlertCircle,
  HeartHandshake,
  Layers,
  IndianRupee,
  Clock,
  CheckCircle2
} from 'lucide-react';
import SearchableSelect from '../../Components/common/SearchableSelect';

// Helper to calculate exact age from Date of Birth
const calculateAgeFromDob = (dobString) => {
  if (!dobString) return '';
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 ? Math.min(125, age) : 0;
};

const LeadRegistrationCC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { socket } = useSocket();
  const [step, setStep] = useState(() => {
    const saved = sessionStorage.getItem('leadReg_step');
    const parsed = saved ? parseInt(saved, 10) : null;
    if (parsed) return parsed;
    // Extract ID from pathname as useParams might not be available yet if used outside, but it is available here.
    const urlParts = window.location.pathname.split('/');
    const hasId = urlParts.length > 3 && urlParts[urlParts.length - 1] !== 'register';
    return hasId ? 2 : 1;
  });
  const [loading, setLoading] = useState(false);
  const [validatingGenerator, setValidatingGenerator] = useState(false);
  const [intakeCategory, setIntakeCategory] = useState(() => {
    const saved = sessionStorage.getItem('leadReg_intakeCategory');
    return saved || 'surgical';
  });

  // Lead Generator State
  const [generatorMobile, setGeneratorMobile] = useState(() => sessionStorage.getItem('leadReg_generatorMobile') || '');
  const [generatorType, setGeneratorType] = useState(() => sessionStorage.getItem('leadReg_generatorType') || '');
  const [validatedGenerator, setValidatedGenerator] = useState(() => {
    const saved = sessionStorage.getItem('leadReg_generator');
    return saved ? JSON.parse(saved) : null;
  });
  const [remoteLead, setRemoteLead] = useState(() => {
    const saved = sessionStorage.getItem('leadReg_remoteLead');
    return saved ? JSON.parse(saved) : null;
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [hospitals, setHospitals] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // 🔍 Phone Lookup & Existing Patient History State
  const [isSearchingPhone, setIsSearchingPhone] = useState(false);
  const [lookupData, setLookupData] = useState(null);
  const [selectedPatientProfile, setSelectedPatientProfile] = useState(null);
  const [isNewPatientUnderSamePhone, setIsNewPatientUnderSamePhone] = useState(false);
  const [isNameDropdownOpen, setIsNameDropdownOpen] = useState(false);
  const nameDropdownRef = useRef(null);

  const topRef = useRef(null);

  // Refs for auto-scroll to error fields
  const fieldRefs = {
    patientName: useRef(null),
    patientPhone: useRef(null),
    patientAge: useRef(null),
    treatmentRequired: useRef(null),
    patientAddress: useRef(null),
    eligibilityMode: useRef(null),
    opheService: useRef(null),
  };

  // 🩺 OPHE Catalog State
  const [opheCategories, setOpheCategories] = useState([]);
  const [opheServices, setOpheServices] = useState([]);
  const [loadingOpheCatalog, setLoadingOpheCatalog] = useState(false);
  const [selectedOpheCategoryFilter, setSelectedOpheCategoryFilter] = useState('all');

  // Auto-scroll to top when step changes
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [step]);

  // 📡 REAL-TIME: Listen for patient submissions
  useEffect(() => {
    if (!socket || !remoteLead) return;

    socket.on('lead_updated', (data) => {

      if (String(data.leadId) === String(remoteLead._id)) {
        // Only trigger document submission alert if documents were actually uploaded
        const isDocumentSubmission = Boolean(
          data.type === 'medical_report' ||
          data.documentCount ||
          (data.documents && Array.isArray(data.documents) && data.documents.length > (remoteLead.documents?.length || 0))
        );

        if (isDocumentSubmission) {
          const patientName = data.patientName || remoteLead.patientName || 'Patient';
          toast.success(`Submission Received: ${patientName} has uploaded documents!`, {
            duration: 6000,
            icon: '✅'
          });

          // Update the remoteLead state with new documents and status
          setRemoteLead(prev => ({
            ...prev,
            documents: data.documents || prev.documents,
            updatedAt: new Date().toISOString()
          }));

          // 🚀 AUTOMATIC NAVIGATION: Move to Step 5 (Audit) instead of Pipeline
          if (data.type === 'medical_report') {
            setTimeout(() => {
              toast.success('Protocols Received! Moving to Audit Stage...', { icon: '📊' });
              setStep(5);
            }, 2000);
          }
        }
      }
    });

    return () => {
      socket.off('lead_updated');
    };
  }, [socket, remoteLead]);

  // Form State
  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('leadReg_formData');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const rawAge = parsed.patientAge;
        let sanitizedAge = '';
        if (rawAge !== undefined && rawAge !== null && rawAge !== '') {
          const parsedInt = parseInt(rawAge, 10);
          if (!isNaN(parsedInt) && parsedInt >= 0) {
            sanitizedAge = Math.min(125, parsedInt);
          }
        }
        return {
          alternativeCC: '',
          linkExpiry: '30',
          leadType: '',
          assignedHospital: '',
          isManualPastEntry: false,
          opheMedicalInfo: {
            diabetesStatus: '',
            diabetesSugarValue: '',
            bpSystolic: '',
            bpDiastolic: '',
            bpStatus: '',
            thyroidStatus: '',
            thyroidType: '',
            thyroidValueReport: '',
            otherConditions: '',
            currentMedication: '',
            ...(parsed.opheMedicalInfo || {})
          },
          opheServiceDetails: {
            categoryId: '',
            categoryName: '',
            serviceId: '',
            serviceName: '',
            planDuration: 'perDay',
            price: 0,
            customNotes: '',
            ...(parsed.opheServiceDetails || {})
          },
          ...parsed,
          patientAge: sanitizedAge
        };
      } catch (e) {
        console.error('Error parsing leadReg_formData from sessionStorage', e);
      }
    }
    return {
      patientName: '',
      patientPhone: '',
      patientEmail: '',
      patientAge: '',
      patientGender: 'male',
      bloodGroup: '',
      dateOfBirth: '',
      emergencyContact: '',
      abhaId: '',
      patientAddress: '',
      treatmentRequired: '',
      comorbidities: '',
      eligibilityMode: '',
      ayushmanCardImage: null,
      patientIdProof: null,
      medicalReports: [],
      notes: '',
      remoteIntake: false,
      alternativePhone: '',
      alternativeEmail: '',
      alternativeCC: '',
      linkExpiry: '30',
      leadType: '',
      assignedHospital: '',
      subServiceId: '',
      isManualPastEntry: false,
      opheServiceDetails: {
        categoryId: '',
        categoryName: '',
        serviceId: '',
        serviceName: '',
        planDuration: 'perDay',
        price: 0,
        customNotes: ''
      },
      opheMedicalInfo: {
        diabetesStatus: '',
        diabetesSugarValue: '',
        bpSystolic: '',
        bpDiastolic: '',
        bpStatus: '',
        thyroidStatus: '',
        thyroidType: '',
        thyroidValueReport: '',
        otherConditions: '',
        currentMedication: ''
      }
    };
  });

  // Keep Session Storage in sync with React State
  useEffect(() => {
    sessionStorage.setItem('leadReg_step', step.toString());
    sessionStorage.setItem('leadReg_generatorMobile', generatorMobile);
    sessionStorage.setItem('leadReg_generatorType', generatorType);
    sessionStorage.setItem('leadReg_intakeCategory', intakeCategory);
    if (validatedGenerator) sessionStorage.setItem('leadReg_generator', JSON.stringify(validatedGenerator));
    else sessionStorage.removeItem('leadReg_generator');

    if (remoteLead) sessionStorage.setItem('leadReg_remoteLead', JSON.stringify(remoteLead));
    else sessionStorage.removeItem('leadReg_remoteLead');

    sessionStorage.setItem('leadReg_formData', JSON.stringify(formData));
  }, [step, generatorMobile, generatorType, validatedGenerator, remoteLead, formData, intakeCategory]);

  const queryParams = new URLSearchParams(window.location.search);
  const isWebLead =
    remoteLead?.source === 'DOXEZ' ||
    formData?.source === 'DOXEZ' ||
    queryParams.get('source') === 'web' ||
    validatedGenerator?.name?.includes('DOXEZ') ||
    validatedGenerator?.type === 'doxez_lead' ||
    generatorType === 'doxez_lead';

  // 🛡️ RE-ENFORCEMENT: Never allow Step 3 (Intent) unless generator type is doctor or hospital
  useEffect(() => {
    if (step === 3 && validatedGenerator?.type !== 'doctor' && validatedGenerator?.type !== 'hospital') {
      setStep(4);
    }
  }, [step, validatedGenerator]);

  // 🛡️ RE-ENFORCEMENT: Lock Step 1 for Resumed leads or Website leads
  useEffect(() => {
    if ((id || isWebLead) && step === 1) {
      setStep(2);
    }
    if (isWebLead && !validatedGenerator) {
      setValidatedGenerator({
        _id: 'doxez_internal',
        name: 'DOXEZ Internal System',
        type: 'doxez_lead',
        mobile: '0000000000'
      });
      setGeneratorMobile('0000000000');
      setGeneratorType('doxez_lead');
    }
  }, [step, isWebLead, validatedGenerator, user, id]);

  // Read phone and name from URL query parameters (e.g., from Exotel incoming call screen pop)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phoneParam = params.get('phone');
    const nameParam = params.get('name');

    if (phoneParam || nameParam) {
      setFormData(prev => ({
        ...prev,
        ...(phoneParam && { patientPhone: phoneParam }),
        ...(nameParam && nameParam !== 'Unknown Caller' && { patientName: nameParam })
      }));
    }
  }, []);

  // 🔄 Explicit Reset Handler (for starting a new registration from scratch)
  const handleResetRegistration = () => {
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('leadReg_')) sessionStorage.removeItem(key);
    });
    setStep(1);
    setGeneratorMobile('');
    setGeneratorType('');
    setValidatedGenerator(null);
    setRemoteLead(null);
    setIntakeCategory('surgical');
    setFormData({
      patientName: '',
      patientPhone: '',
      patientEmail: '',
      patientAge: '',
      patientGender: 'male',
      bloodGroup: '',
      dateOfBirth: '',
      emergencyContact: '',
      abhaId: '',
      patientAddress: '',
      treatmentRequired: '',
      comorbidities: '',
      eligibilityMode: '',
      ayushmanCardImage: null,
      patientIdProof: null,
      medicalReports: [],
      notes: '',
      remoteIntake: false,
      alternativePhone: '',
      alternativeEmail: '',
      alternativeCC: '',
      linkExpiry: '30',
      leadType: '',
      assignedHospital: '',
      subServiceId: '',
      isManualPastEntry: false
    });
    setFieldErrors({});
    toast.success('Registration reset to initial step');
  };

  // 🔄 RESUMPTION LOGIC: Load existing lead if ID is provided, else retain active session
  useEffect(() => {
    const handleInit = async () => {
      if (!id) {
        // Retain saved session state across reloads
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/leads/${id}`);
        const lead = response.data.data;

        // 1. Set Remote Lead
        setRemoteLead(lead);

        // 2. Set Step
        const queryParams = new URLSearchParams(window.location.search);
        const forceAudit = queryParams.get('submission') === 'true';

        if (forceAudit || (lead.documents && lead.documents.length > 0)) {
          setStep(5);
        } else if (lead.source === 'DOXEZ') {
          // 🚀 WEBSITE LEAD: Skip Step 1 (Source) and go to Step 2 (Patient)
          setStep(2);
        } else if (lead.registrationStep && lead.registrationStep > 1) {
          setStep(lead.registrationStep);
        } else if (lead.leadGeneratedById) {
          // 🚀 GENERATOR ALREADY ASSIGNED: Skip Step 1
          setStep(2);
        }

        // 3. Populate Generator Info
        if (lead.source === 'DOXEZ') {
          setValidatedGenerator({
            id: lead.createdBy?._id || lead.createdBy,
            name: 'DOXEZ Internal (Web)',
            type: 'marketing',
            mobile: lead.patientPhone,
            email: lead.patientEmail
          });
          setGeneratorType('marketing');
        } else {
          const creatorName = lead.createdBy ? `${lead.createdBy.firstName || ''} ${lead.createdBy.lastName || ''}`.trim() : '';
          const creatorPhone = lead.createdBy?.phone || 'Verified';
          const actualRole = lead.createdBy?.role || lead.createdByRole || 'doctor';
          const generatorRole = actualRole === 'marketing_agent' ? 'marketing' : actualRole;

          setValidatedGenerator({
            id: lead.leadGeneratedById || lead.createdBy?._id,
            name: lead.leadGeneratedByName || creatorName || 'Verified Partner',
            type: lead.leadGeneratedByType || generatorRole,
            mobile: creatorPhone,
            email: lead.createdBy?.email || ''
          });
          setGeneratorType(lead.leadGeneratedByType || generatorRole);

          if (lead.createdBy?.phone && ['marketing_agent', 'marketing', 'doctor', 'hospital'].includes(actualRole)) {
            setGeneratorMobile(lead.createdBy.phone);
          } else {
            setGeneratorMobile('');
          }
        }

        // 4. Populate Form Data
        setFormData(prev => ({
          ...prev,
          patientName: lead.patientName || '',
          patientPhone: lead.patientPhone || '',
          patientEmail: lead.patientEmail || '',
          patientAge: (lead.patientAge !== undefined && lead.patientAge !== null && lead.patientAge !== '' && !isNaN(parseInt(lead.patientAge, 10)) && parseInt(lead.patientAge, 10) >= 0) ? Math.min(125, parseInt(lead.patientAge, 10)) : '',
          patientGender: lead.patientGender || 'male',
          bloodGroup: lead.bloodGroup || '',
          dateOfBirth: lead.dateOfBirth || '',
          emergencyContact: lead.emergencyContact || '',
          abhaId: lead.abhaId || '',
          patientAddress: lead.patientAddress || lead.city || '',
          treatmentRequired: lead.treatmentRequired || '',
          comorbidities: lead.comorbidities || '',
          eligibilityMode: lead.hasAyushmanCard ? 'ayushman' : (lead.paymentType?.includes('Insurance') ? 'insurance' : 'cash'),
          ayushmanCardImage: lead.documents?.find(d => d.type === 'ayushman_card')?.url || lead.documents?.find(d => d.name === 'Ayushman Card')?.url || null,
          patientIdProof: lead.documents?.find(d => d.type === 'id_proof')?.url || lead.documents?.find(d => d.name === 'ID Proof')?.url || null,
          medicalReports: lead.documents?.filter(d => d.type === 'medical_report').map(d => ({ url: d.url, name: d.name })) || [],
          notes: lead.notes?.[0]?.content || '',
          assignedHospital: lead.assignedHospital?._id || lead.assignedHospital || '',
          leadType: lead.leadType || '',
          subServiceId: lead.subServiceId?._id || lead.subServiceId || '',
          isManualPastEntry: lead.isManualPastEntry || false,
          opheServiceDetails: {
            categoryId: lead.opheServiceDetails?.categoryId?._id || lead.opheServiceDetails?.categoryId || '',
            categoryName: lead.opheServiceDetails?.categoryName || '',
            serviceId: lead.opheServiceDetails?.serviceId?._id || lead.opheServiceDetails?.serviceId || '',
            serviceName: lead.opheServiceDetails?.serviceName || '',
            planDuration: lead.opheServiceDetails?.planDuration || 'perDay',
            price: lead.opheServiceDetails?.price || 0,
            customNotes: lead.opheServiceDetails?.customNotes || ''
          },
          opheMedicalInfo: {
            diabetesStatus: lead.opheMedicalInfo?.diabetesStatus || '',
            diabetesSugarValue: lead.opheMedicalInfo?.diabetesSugarValue || '',
            bpSystolic: lead.opheMedicalInfo?.bpSystolic || '',
            bpDiastolic: lead.opheMedicalInfo?.bpDiastolic || '',
            bpStatus: lead.opheMedicalInfo?.bpStatus || '',
            thyroidStatus: lead.opheMedicalInfo?.thyroidStatus || '',
            thyroidType: lead.opheMedicalInfo?.thyroidType || '',
            thyroidValueReport: lead.opheMedicalInfo?.thyroidValueReport || '',
            otherConditions: lead.opheMedicalInfo?.otherConditions || '',
            currentMedication: lead.opheMedicalInfo?.currentMedication || ''
          }
        }));

        if (lead.leadType === 'appointment' || lead.pipelineChoice === 'appointment') {
          setIntakeCategory('appointment');
        } else if (lead.leadType === 'ophe' || lead.pipelineChoice === 'ophe' || lead.leadGeneratedByType === 'ophe') {
          setIntakeCategory('ophe');
        }

        toast.success(`Resuming registration for ${lead.patientName}`);
      } catch (error) {
        console.error('Failed to fetch lead for resumption:', error);
        toast.error('Failed to load lead progress');
      } finally {
        setLoading(false);
      }
    };

    handleInit();
  }, [id]);

  // 🏥 Fetch Hospitals for Doctor Selection
  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const response = await api.get('/hospitals?limit=500');
        setHospitals(response.data.data.hospitals || []);
      } catch (error) {
        console.error('Failed to fetch hospitals:', error);
      }
    };
    fetchHospitals();
  }, []);

  // 🩺 OPHE: Fetch Categories and Services for OPHE intake
  useEffect(() => {
    if (intakeCategory === 'ophe') {
      fetchOpheCatalog();
    }
  }, [intakeCategory]);

  const fetchOpheCatalog = async () => {
    try {
      setLoadingOpheCatalog(true);
      const [catsRes, servsRes] = await Promise.all([
        api.get('/ophe/categories?status=active'),
        api.get('/ophe/services?status=active')
      ]);
      if (catsRes.data?.success) {
        setOpheCategories(catsRes.data.data || []);
      }
      if (servsRes.data?.success) {
        setOpheServices(servsRes.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch OPHE catalog:', error);
    } finally {
      setLoadingOpheCatalog(false);
    }
  };

  const handleSelectOpheService = (service, duration = 'perDay') => {
    if (!service) {
      setFormData(prev => ({
        ...prev,
        opheServiceDetails: {
          categoryId: '',
          categoryName: '',
          serviceId: '',
          serviceName: '',
          planDuration: 'perDay',
          price: 0,
          customNotes: ''
        },
        treatmentRequired: ''
      }));
      return;
    }

    const catName = service.categoryId?.name || opheCategories.find(c => c._id === (service.categoryId?._id || service.categoryId))?.name || 'OPHE Care';
    const price = service.pricing?.[duration] || 0;
    const durationLabels = {
      perDay: 'Per Day',
      days7: '7 Days Package',
      days15: '15 Days Package',
      days30: '30 Days Package'
    };
    const durationLabel = durationLabels[duration] || 'Per Day';

    setFormData(prev => ({
      ...prev,
      treatmentRequired: `${service.serviceName} (${durationLabel})`,
      opheServiceDetails: {
        categoryId: service.categoryId?._id || service.categoryId,
        categoryName: catName,
        serviceId: service._id,
        serviceName: service.serviceName,
        planDuration: duration,
        price: price,
        customNotes: prev.opheServiceDetails?.customNotes || ''
      }
    }));
    setFieldErrors(prev => ({ ...prev, opheService: '' }));
  };

  const handleOpheDurationChange = (duration) => {
    if (!formData.opheServiceDetails?.serviceId) return;
    const currentService = opheServices.find(s => s._id === formData.opheServiceDetails.serviceId);
    if (currentService) {
      handleSelectOpheService(currentService, duration);
    }
  };

  // 💾 SYNC LOGIC: Persist progress to backend
  const syncProgress = async (nextStep, customPayload = {}) => {
    if (!remoteLead && nextStep <= 2) {
      setStep(nextStep);
      return;
    }

    // Validation logic for specific steps
    if (step === 3 && nextStep === 4) {
      if ((validatedGenerator?.type === 'doctor' || validatedGenerator?.type === 'admin') && !formData.assignedHospital) {
        toast.error("Please select a Target Operating Facility for this doctor's lead.");
        return;
      }
    }

    const cleanedPhone = formData.patientPhone.replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10);

    // Prepare payload for auto-save
    const payload = {
      patientName: formData.patientName,
      patientPhone: cleanedPhone,
      patientEmail: formData.patientEmail || null,
      patientAge: (formData.patientAge && !isNaN(parseInt(formData.patientAge))) ? parseInt(formData.patientAge, 10) : null,
      patientGender: formData.patientGender,
      bloodGroup: formData.bloodGroup || null,
      dateOfBirth: intakeCategory === 'ophe' ? undefined : (formData.dateOfBirth || null),
      emergencyContact: formData.emergencyContact || null,
      abhaId: intakeCategory === 'ophe' ? undefined : (formData.abhaId || null),
      patientAddress: formData.patientAddress,
      treatmentRequired: intakeCategory === 'ophe' ? (formData.treatmentRequired || formData.opheServiceDetails?.serviceName || null) : (formData.treatmentRequired || null),
      comorbidities: intakeCategory === 'ophe' ? undefined : (formData.comorbidities || null),
      registrationStep: nextStep,
      assignedHospital: formData.assignedHospital || null,
      leadType: intakeCategory === 'ophe' ? 'ophe' : (formData.leadType || 'lead_only'),
      pipelineChoice: intakeCategory === 'ophe' ? 'ophe' : (formData.pipelineChoice || (intakeCategory === 'appointment' ? 'appointment' : 'surgical')),
      subServiceId: formData.subServiceId || null,
      isManualPastEntry: formData.isManualPastEntry || false,
      source: intakeCategory === 'ophe' ? 'ophe' : (formData.isManualPastEntry ? 'past_surgery' : (formData.source || 'customer_care')),
      leadGeneratedByType: intakeCategory === 'ophe' ? 'ophe' : (formData.isManualPastEntry ? 'past_surgery' : (formData.leadGeneratedByType || 'customer_care')),
      leadGeneratedByName: intakeCategory === 'ophe' ? 'OPHE' : (validatedGenerator?.name || formData.leadGeneratedByName || 'Direct Intake'),
      isDoctorPerformingSurgery: (validatedGenerator?.type === 'doctor' && (formData.leadType === 'surgery' || customPayload?.leadType === 'surgery')) ? 'Yes' : 'No',
      isDoctorOnlyProvidingLead: (validatedGenerator?.type === 'doctor' && (formData.leadType === 'lead_only' || customPayload?.leadType === 'lead_only')) ? 'Yes' : 'No',
      isSurgeryPerformedInTheirHospital: (validatedGenerator?.type === 'hospital' && (formData.leadType === 'surgery' || customPayload?.leadType === 'surgery')) ? 'Yes' : 'No',
      isHospitalOnlyProvidingLead: (validatedGenerator?.type === 'hospital' && (formData.leadType === 'lead_only' || customPayload?.leadType === 'lead_only')) ? 'Yes' : 'No',
      opheServiceDetails: intakeCategory === 'ophe' ? formData.opheServiceDetails : undefined,
      opheMedicalInfo: formData.opheMedicalInfo,
      ...customPayload
    };

    try {
      if (remoteLead) {
        await api.patch(`/leads/${remoteLead._id}`, payload);
      } else {
        // Validation for creation
        if (!cleanedPhone || cleanedPhone.length !== 10) {
          console.warn('Sync aborted: Invalid phone number for lead creation');
          return;
        }
        // Create initial lead if moving from step 2 to 3
        const createPayload = {
          ...payload,
          leadGeneratedByType: intakeCategory === 'ophe' ? 'ophe' : (validatedGenerator?.type || 'marketing'),
          leadGeneratedById: validatedGenerator?.id || validatedGenerator?._id,
          leadGeneratedByName: intakeCategory === 'ophe' ? 'OPHE' : validatedGenerator?.name,
          source: intakeCategory === 'ophe' ? 'ophe' : (validatedGenerator?.type || 'marketing'),
          pipelineChoice: intakeCategory === 'ophe' ? 'ophe' : (formData.pipelineChoice || (intakeCategory === 'appointment' ? 'appointment' : 'surgical')),
          createdBy: user._id
        };

        const response = await api.post('/leads', createPayload);
        setRemoteLead(response.data.data);
      }
      setStep(nextStep);
    } catch (error) {
      console.error('Failed to sync progress:', error.response?.data || error.message);
      toast.error('Failed to save progress, but moving to next step...');
      setStep(nextStep);
    }
  };



  // Step 1: Validate Generator
  const handleValidateGenerator = async () => {
    if (!generatorMobile || generatorMobile.length < 10) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }

    setValidatingGenerator(true);
    try {
      const response = await api.get(`/partners/validate-generator?mobile=${generatorMobile}&type=${generatorType}`);
      const result = response.data.data;

      if (result.registered) {
        setValidatedGenerator(result);

        // AUTO-ASSIGN: If it's a hospital lead, set the assignedHospital immediately
        if (result.type === 'hospital') {
          setFormData(prev => ({ ...prev, assignedHospital: result.id }));
        }

        toast.success(`Validated: ${result.name}`);
        // 🚀 Step 1 (Source) always goes to Step 2 (Patient) now
        setStep(2);
      } else {
        setValidatedGenerator(null);
        toast.error('Lead Generator not registered. Please register them first.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Validation failed');
    } finally {
      setValidatingGenerator(false);
    }
  };

  const handlePatientContinue = async () => {
    // ✅ Required field validation with field-level error highlighting
    const cleanedPhone = (formData.patientPhone || '').replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10);

    const errors = {};
    if (!formData.patientName?.trim()) errors.patientName = 'Patient name is required';
    if (!cleanedPhone || cleanedPhone.length !== 10) errors.patientPhone = 'Valid 10-digit mobile number is required';
    const parsedAge = parseInt(formData.patientAge, 10);
    if (!formData.patientAge || isNaN(parsedAge) || parsedAge < 0) errors.patientAge = 'Please enter a valid age (0–125)';
    if (intakeCategory === 'ophe') {
      if (!formData.opheServiceDetails?.serviceId) {
        errors.opheService = 'Please choose an OPHE service package';
      }
    } else {
      if (!formData.treatmentRequired?.trim()) errors.treatmentRequired = 'Treatment / disease is required';
    }
    if (!formData.patientAddress?.trim()) errors.patientAddress = 'Address / city is required';

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Scroll to the first empty field
      const firstErrorField = Object.keys(errors)[0];
      const ref = fieldRefs[firstErrorField];
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.current.focus();
      }
      toast.error(`Please fill all required fields before continuing.`, {
        icon: '⚠️',
        duration: 3000,
      });
      return;
    }

    // Clear errors on success
    setFieldErrors({});

    // 🚀 Decide next step after Patient Details
    if (intakeCategory === 'ophe') {
      await syncProgress(5); // OPHE skips Step 4 (Medical) -> Goes directly to Step 5 (Confirm)
    } else if (validatedGenerator?.type !== 'doctor' && validatedGenerator?.type !== 'hospital') {
      await syncProgress(4); // Skip Step 3 (Intent) -> Go to Step 4 (Medical)
    } else {
      await syncProgress(3); // Go to Step 3 (Intent)
    }
  };

  const handleCategorySwitch = (category) => {
    setIntakeCategory(category);
    if (category === 'appointment') {
      setFormData(prev => ({
        ...prev,
        leadType: 'appointment',
        pipelineChoice: 'appointment',
        treatmentRequired: prev.treatmentRequired || 'Doctor Consultation'
      }));
      if (step > 2) {
        setStep(2);
      }
    } else if (category === 'ophe') {
      setFormData(prev => ({
        ...prev,
        leadType: 'ophe',
        pipelineChoice: 'ophe',
        treatmentRequired: prev.treatmentRequired === 'Doctor Consultation' ? '' : prev.treatmentRequired
      }));
      if (step === 4) {
        setStep(5);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        leadType: prev.leadType === 'appointment' ? '' : prev.leadType,
        pipelineChoice: 'surgical',
        treatmentRequired: prev.treatmentRequired === 'Doctor Consultation' ? '' : prev.treatmentRequired
      }));
    }
    setFieldErrors({});
  };

  const handleAppointmentSubmit = async () => {
    const cleanedPhone = (formData.patientPhone || '').replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10);

    const errors = {};
    if (!formData.patientName?.trim()) errors.patientName = 'Patient name is required';
    if (!cleanedPhone || cleanedPhone.length !== 10) errors.patientPhone = 'Valid 10-digit mobile number is required';
    const parsedApptAge = parseInt(formData.patientAge, 10);
    if (!formData.patientAge || isNaN(parsedApptAge) || parsedApptAge < 0) errors.patientAge = 'Please enter a valid age (0–125)';
    if (!formData.treatmentRequired?.trim()) errors.treatmentRequired = 'Consultation reason / chief complaints is required';
    if (!formData.patientAddress?.trim()) errors.patientAddress = 'Address / city is required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      const ref = fieldRefs[firstErrorField];
      if (ref?.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        ref.current.focus();
      }
      toast.error('Please fill all required fields before submitting', { icon: '⚠️' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patientName: formData.patientName.trim(),
        patientPhone: cleanedPhone,
        patientEmail: formData.patientEmail?.trim() || null,
        patientAge: parseInt(formData.patientAge, 10),
        patientGender: formData.patientGender || 'male',
        bloodGroup: formData.bloodGroup || null,
        dateOfBirth: formData.dateOfBirth || null,
        patientAddress: formData.patientAddress.trim(),
        city: formData.patientAddress.trim(),
        treatmentRequired: formData.treatmentRequired.trim() || 'Doctor Consultation',
        disease: formData.treatmentRequired.trim() || 'Doctor Consultation',
        chiefComplaints: formData.treatmentRequired.trim() || null,
        comorbidities: formData.comorbidities?.trim() || null,
        leadType: 'appointment',
        pipelineChoice: 'appointment',
        source: validatedGenerator?.type || 'customer_care',
        leadGeneratedByType: validatedGenerator?.type || 'customer_care',
        leadGeneratedById: validatedGenerator?.id || validatedGenerator?._id || null,
        leadGeneratedByName: validatedGenerator?.name || null,
        notes: formData.notes?.trim() || null,
        createdBy: user._id,
        status: 'new'
      };

      const response = remoteLead
        ? await api.patch(`/leads/${remoteLead._id}`, payload)
        : await api.post('/leads', payload);

      toast.success('Doctor Appointment Lead Generated Successfully!');
      const createdLead = response.data.data;

      // Clean session storage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('leadReg_')) sessionStorage.removeItem(key);
      });

      // Obtain associated appointment ID
      let apptId = createdLead?.associatedAppointment?._id || createdLead?.associatedAppointment;
      if (!apptId && createdLead?._id) {
        try {
          const apptRes = await api.get(`/appointments?leadId=${createdLead._id}&limit=1`);
          const appts = apptRes.data?.data?.appointments || apptRes.data?.data || [];
          if (Array.isArray(appts) && appts.length > 0) {
            apptId = appts[0]._id;
          }
        } catch (e) {
          console.warn('Could not fetch newly created appointment by leadId', e);
        }
      }

      if (apptId) {
        navigate(`/appointments/${apptId}?leadId=${createdLead?._id}&from=leads&origin=leads&tab=doctor_type&menu=appointments`);
      } else {
        navigate(`/appointments`);
      }
    } catch (error) {
      console.error('Appointment Lead Creation Error:', error);
      toast.error(error.response?.data?.message || 'Failed to create appointment lead');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let { name, value, type, checked } = e.target;
    if (name === 'patientPhone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    if (name === 'abhaId') {
      value = value.replace(/\D/g, '').slice(0, 14);
    }
    // Prevent age from being negative or exceeding 125, while allowing clearing to empty
    if (name === 'patientAge') {
      if (value === '') {
        value = '';
      } else {
        const clean = value.toString().replace(/[^0-9]/g, '');
        value = clean === '' ? '' : Math.min(125, Math.max(0, parseInt(clean, 10)));
      }
    }
    // Auto-calculate age when Date of Birth is selected
    if (name === 'dateOfBirth') {
      const calcAge = calculateAgeFromDob(value);
      setFormData(prev => ({
        ...prev,
        dateOfBirth: value,
        ...(calcAge !== '' ? { patientAge: calcAge } : {})
      }));
      if (calcAge !== '') {
        setFieldErrors(prev => ({ ...prev, patientAge: '' }));
      }
      return;
    }
    if (type === 'checkbox') {
      if (name === 'paymentType') {
        const updatedPayment = [...formData.paymentType];
        if (checked) {
          updatedPayment.push(value);
        } else {
          const index = updatedPayment.indexOf(value);
          if (index > -1) updatedPayment.splice(index, 1);
        }
        setFormData({ ...formData, paymentType: updatedPayment });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleOpheMedicalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      opheMedicalInfo: {
        ...(prev.opheMedicalInfo || {}),
        [field]: value
      }
    }));
  };

  // 🔍 Click Outside Listener for Name Dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (nameDropdownRef.current && !nameDropdownRef.current.contains(e.target)) {
        setIsNameDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔍 Phone Lookup Effect: Triggers when 10-digit mobile number is entered
  useEffect(() => {
    const clean = String(formData.patientPhone || '').replace(/\D/g, '').slice(-10);
    if (clean.length === 10) {
      const timer = setTimeout(async () => {
        setIsSearchingPhone(true);
        try {
          const res = await api.get(`/leads/lookup-phone/${clean}`);
          const data = res.data?.data;
          setLookupData(data);
          if (data?.patients?.length > 0) {
            const enteredName = formData.patientName?.trim()?.toLowerCase();
            const exactMatch = enteredName ? data.patients.find(p => p.patientName.toLowerCase() === enteredName) : null;
            if (exactMatch && !selectedPatientProfile) {
              handleSelectPatient(exactMatch, false);
            } else if (!selectedPatientProfile) {
              setIsNameDropdownOpen(true);
            }
          } else {
            setIsNameDropdownOpen(false);
          }
        } catch (err) {
          console.error("Phone lookup failed:", err);
          setLookupData(null);
          setIsNameDropdownOpen(false);
        } finally {
          setIsSearchingPhone(false);
        }
      }, 350);

      return () => clearTimeout(timer);
    } else {
      setLookupData(null);
      setSelectedPatientProfile(null);
      setIsNewPatientUnderSamePhone(false);
      setIsNameDropdownOpen(false);
    }
  }, [formData.patientPhone]);

  const handleSelectPatient = (patient, notify = true) => {
    setSelectedPatientProfile(patient);
    setIsNewPatientUnderSamePhone(false);
    setIsNameDropdownOpen(false);

    setFormData(prev => ({
      ...prev,
      patientName: patient.patientName || prev.patientName,
      patientEmail: patient.patientEmail || prev.patientEmail,
      patientAge: (patient.patientAge !== undefined && patient.patientAge !== null && patient.patientAge !== '') ? String(patient.patientAge) : prev.patientAge,
      patientGender: patient.patientGender || prev.patientGender || 'male',
      bloodGroup: patient.bloodGroup || prev.bloodGroup,
      dateOfBirth: patient.dateOfBirth || prev.dateOfBirth,
      emergencyContact: patient.emergencyContact || prev.emergencyContact,
      abhaId: patient.abhaId || prev.abhaId,
      patientAddress: patient.patientAddress || prev.patientAddress,
      treatmentRequired: prev.treatmentRequired || patient.treatmentRequired || (intakeCategory === 'appointment' ? 'Doctor Consultation' : ''),
      chiefComplaints: prev.chiefComplaints || patient.chiefComplaints || '',
      pastMedicalHistory: patient.pastMedicalHistory || prev.pastMedicalHistory,
      comorbidities: patient.comorbidities || prev.comorbidities,
      eligibilityMode: patient.coverageMode || (patient.hasAyushmanCard ? 'ayushman' : prev.eligibilityMode)
    }));

    setFieldErrors(prev => ({
      ...prev,
      patientName: '',
      patientPhone: '',
      patientAge: '',
      patientAddress: ''
    }));

    if (notify) {
      toast.success(`Loaded previous records for ${patient.patientName}!`, { icon: '👤' });
    }
  };

  const handleAddNewPatientUnderSamePhone = () => {
    setSelectedPatientProfile(null);
    setIsNewPatientUnderSamePhone(true);
    setIsNameDropdownOpen(false);

    setFormData(prev => ({
      ...prev,
      patientName: '',
      patientEmail: '',
      patientAge: '',
      patientGender: 'male',
      bloodGroup: '',
      dateOfBirth: '',
      emergencyContact: '',
      abhaId: '',
      patientAddress: '',
      treatmentRequired: intakeCategory === 'appointment' ? 'Doctor Consultation' : '',
      chiefComplaints: '',
      pastMedicalHistory: '',
      comorbidities: '',
      eligibilityMode: ''
    }));

    toast(`Ready to enter new patient details under ${formData.patientPhone}`, { icon: '➕' });
    if (fieldRefs.patientName?.current) {
      fieldRefs.patientName.current.focus();
    }
  };

  const handleResetSelection = () => {
    setSelectedPatientProfile(null);
    setIsNewPatientUnderSamePhone(false);
    setIsNameDropdownOpen(true);
  };

  const handleFileUpload = async (files, type) => {
    if (!files || files.length === 0) return;

    // Check file size (50MB limit)
    const MAX_SIZE = 50 * 1024 * 1024;
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`File "${file.name}" exceeds 50MB limit. Please upload smaller files.`);
        return;
      }
    }

    // Limit check for medical
    if (type === 'medical' && (formData.medicalReports.length + files.length > 15)) {
      toast.error('Maximum 15 reports allowed');
      return;
    }

    const fileFormData = new FormData();
    files.forEach(file => {
      fileFormData.append('documents', file);
    });

    try {
      setLoading(true);
      const response = await api.post('/upload', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedFiles = response.data.data.files;
      if (!uploadedFiles || uploadedFiles.length === 0) {
        throw new Error('No files returned from server');
      }

      if (type === 'ayushman') {
        setFormData(prev => ({ ...prev, ayushmanCardImage: uploadedFiles[0].url }));
        toast.success("Ayushman Card Attached");
      } else if (type === 'id_proof') {
        setFormData(prev => ({ ...prev, patientIdProof: uploadedFiles[0].url }));
        toast.success("Identity Proof Attached");
      } else {
        const newReports = uploadedFiles.map(f => ({ url: f.url, name: f.filename }));
        setFormData(prev => ({
          ...prev,
          medicalReports: [...prev.medicalReports, ...newReports]
        }));
        toast.success(`${uploadedFiles.length} Reports Added`);
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const initiateRemoteProtocol = async () => {
    if (!formData.patientName || !formData.patientPhone) {
      toast.error('Patient Name and Phone are required for protocol');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patientName: formData.patientName,
        patientPhone: formData.patientPhone.replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10),
        patientEmail: formData.patientEmail || null,
        patientAge: formData.patientAge ? parseInt(formData.patientAge, 10) : null,
        patientGender: formData.patientGender,
        bloodGroup: formData.bloodGroup || null,
        dateOfBirth: formData.dateOfBirth || null,
        emergencyContact: formData.emergencyContact || null,
        abhaId: formData.abhaId || null,
        patientAddress: formData.patientAddress,
        treatmentRequired: formData.treatmentRequired,
        city: formData.patientAddress,
        hasAyushmanCard: formData.eligibilityMode === 'ayushman',
        paymentType: formData.eligibilityMode === 'cash' ? ['Cash'] : formData.eligibilityMode === 'insurance' ? ['Insurance'] : [],
        leadGeneratedByType: validatedGenerator?.type || 'marketing',
        leadGeneratedById: validatedGenerator?.id || validatedGenerator?._id || null,
        leadGeneratedByName: validatedGenerator?.name || 'DOXEZ Internal (Web)',
        source: validatedGenerator?.type || 'marketing',
        notes: formData.notes,
        createdBy: user._id,
        status: 'new' // Initial status
      };

      const response = await api.post('/leads', payload);
      setRemoteLead(response.data.data);
      toast.success('Protocol ID Generated. Launch channels below.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Protocol failed');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailProtocol = async () => {
    if (!remoteLead) return;

    const targetEmail = formData.alternativeEmail || validatedGenerator?.email;
    if (!targetEmail) {
      toast.error('Recipient email address missing. Please enter an alternative email.');
      return;
    }

    setSendingEmail(true);
    try {
      // 🔐 ENCRYPTED: Encrypt lead ID before embedding in email share URL
      const encId = await getEncryptedLeadId(remoteLead._id);
      const shareUrl = `${window.location.origin}/clinical-intake/${encId}`;
      await api.post(`/leads/${remoteLead._id}/notify-clinical-link`, {
        channel: 'email',
        shareUrl,
        alternativeEmail: targetEmail,
        ccEmail: formData.alternativeCC,
        expiryTime: formData.linkExpiry,
        generatorName: validatedGenerator?.name,
        generatorPhone: validatedGenerator?.mobile,
        generatorEmail: validatedGenerator?.email
      });
      toast.success('Protocol Email dispatched via SMTP');
    } catch (error) {
      console.error('SMTP Protocol Failure:', error);
      toast.error(error.response?.data?.message || 'Failed to dispatch SMTP mail');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async () => {
    const cleanedPhone = formData.patientPhone.replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10);

    // Validation
    if (!formData.patientName || !cleanedPhone || cleanedPhone.length !== 10) {
      toast.error('A valid 10-digit patient phone number is required');
      return;
    }

    if (intakeCategory !== 'ophe' && !formData.treatmentRequired) {
      toast.error('Treatment/Disease information is required');
      return;
    }

    // patientAge and patientAddress are recommended but optional for Web Leads to prevent blocking the workflow
    // They can be updated later in the Lead Detail page by the CC agent.

    setLoading(true);
    try {
      // Create a clean payload with only the fields we want to save
      const payload = {
        patientName: formData.patientName,
        patientPhone: formData.patientPhone.replace(/\D/g, '').replace(/^(0|91|\+91)/, '').slice(-10),
        patientEmail: formData.patientEmail || null,
        patientAge: formData.patientAge ? parseInt(formData.patientAge, 10) : null,
        patientGender: formData.patientGender,
        bloodGroup: formData.bloodGroup || null,
        dateOfBirth: intakeCategory === 'ophe' ? undefined : (formData.dateOfBirth || null),
        emergencyContact: formData.emergencyContact || null,
        abhaId: intakeCategory === 'ophe' ? undefined : (formData.abhaId || null),
        patientAddress: formData.patientAddress,
        treatmentRequired: intakeCategory === 'ophe' ? (formData.treatmentRequired || formData.opheServiceDetails?.serviceName || 'OPHE Care') : formData.treatmentRequired,
        comorbidities: intakeCategory === 'ophe' ? undefined : (formData.comorbidities || null),
        city: formData.patientAddress || 'N/A',
        subServiceId: formData.subServiceId || null,
        opheServiceDetails: intakeCategory === 'ophe' ? formData.opheServiceDetails : undefined,
        opheMedicalInfo: formData.opheMedicalInfo,

        // Map eligibility to existing fields
        hasAyushmanCard: formData.eligibilityMode === 'ayushman',
        paymentType: formData.eligibilityMode === 'cash' ? ['Cash'] : formData.eligibilityMode === 'insurance' ? ['Insurance'] : [],

        // Lead Generator Info
        leadGeneratedByType: intakeCategory === 'ophe' ? 'ophe' : (validatedGenerator?.type || 'marketing'),
        leadGeneratedById: validatedGenerator?.id || validatedGenerator?._id || null,
        leadGeneratedByName: intakeCategory === 'ophe' ? 'OPHE' : (validatedGenerator?.name || 'DOXEZ Internal (Web)'),
        source: intakeCategory === 'ophe' ? 'ophe' : (validatedGenerator?.type || 'marketing'),
        leadType: intakeCategory === 'ophe' ? 'ophe' : (formData.leadType || 'lead_only'),
        pipelineChoice: intakeCategory === 'ophe' ? 'ophe' : (formData.pipelineChoice || (intakeCategory === 'appointment' ? 'appointment' : 'surgical')),

        // Call Notes
        notes: formData.notes,
        createdBy: user._id,
        registrationStep: 5
      };

      // Add documents if any
      const documents = [];
      if (formData.ayushmanCardImage) {
        documents.push({ name: 'Ayushman Card', type: 'ayushman_card', url: formData.ayushmanCardImage });
      }
      if (formData.patientIdProof) {
        documents.push({ name: 'ID Proof', type: 'id_proof', url: formData.patientIdProof });
      }
      if (formData.medicalReports && formData.medicalReports.length > 0) {
        formData.medicalReports.forEach(rep => {
          documents.push({ name: rep.name || 'Medical Report', type: 'medical_report', url: rep.url });
        });
      }
      if (documents.length > 0) {
        payload.documents = documents;
      }

      const response = remoteLead
        ? await api.patch(`/leads/${remoteLead._id}`, payload)
        : await api.post('/leads', payload);

      const leadId = response.data.data._id;

      // 🏁 Include clinical flags in final verification
      if (intakeCategory === 'ophe') {
        await api.patch(`/leads/${leadId}`, {
          leadType: 'ophe',
          pipelineChoice: 'ophe',
          source: 'ophe',
          leadGeneratedByType: 'ophe',
          treatmentRequired: formData.treatmentRequired || formData.opheServiceDetails?.serviceName || 'OPHE Care',
          opheServiceDetails: formData.opheServiceDetails,
          opheMedicalInfo: formData.opheMedicalInfo
        });
      } else {
        await api.patch(`/leads/${leadId}`, {
          leadType: formData.leadType || 'lead_only',
          assignedHospital: formData.assignedHospital || null,
          isDoctorPerformingSurgery: (validatedGenerator?.type === 'doctor' && formData.leadType === 'surgery') ? 'Yes' : 'No',
          isDoctorOnlyProvidingLead: (validatedGenerator?.type === 'doctor' && formData.leadType === 'lead_only') ? 'Yes' : 'No',
          isSurgeryPerformedInTheirHospital: (validatedGenerator?.type === 'hospital' && formData.leadType === 'surgery') ? 'Yes' : 'No',
          isHospitalOnlyProvidingLead: (validatedGenerator?.type === 'hospital' && formData.leadType === 'lead_only') ? 'Yes' : 'No',
        });
      }

      // If Remote Intake is active, trigger the notification (Server side backup)
      if (formData.remoteIntake) {
        try {
          // 🔐 ENCRYPTED: Encrypt lead ID before embedding in whatsapp share URL
          const encId = await getEncryptedLeadId(leadId);
          const shareUrl = `${window.location.origin}/clinical-intake/${encId}`;
          await api.post(`/leads/${leadId}/notify-clinical-link`, {
            channel: 'whatsapp',
            shareUrl,
            alternativePhone: formData.alternativePhone,
            alternativeEmail: formData.alternativeEmail
          });
          toast.success("Lead Saved & Protocols Dispatched!");
        } catch (notifyError) {
          console.error("Link notification failed:", notifyError);
          toast.success("Lead created. Please use manual protocols if needed.");
        }
      } else {
        toast.success('Lead Registered Successfully!');
      }

      // Clear Session Storage Locks after successful completion
      sessionStorage.removeItem('leadReg_step');
      sessionStorage.removeItem('leadReg_generatorMobile');
      sessionStorage.removeItem('leadReg_generatorType');
      sessionStorage.removeItem('leadReg_generator');
      sessionStorage.removeItem('leadReg_remoteLead');
      if (intakeCategory === 'ophe') {
        navigate('/ophe/pipeline');
      } else {
        const registeredLead = response.data.data;
        const surgId = registeredLead?.associatedSurgery?._id || leadId;
        navigate(`/surgeries/${surgId}?leadId=${leadId}&from=leads&origin=leads&tab=pipeline&menu=leads`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8" ref={topRef}>
      <div className="max-w-4xl mx-auto">

        {/* Header Section */}
        <div className="text-center mb-8 sm:mb-12 relative">
          {!id && !isWebLead && (step > 1 || validatedGenerator) && (
            <div className="sm:absolute sm:right-0 sm:top-2 mb-4 sm:mb-0 flex justify-center">
              <button
                type="button"
                onClick={handleResetRegistration}
                className="px-3.5 py-1.5 bg-white border border-slate-200 hover:border-red-300 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-xl text-[11px] font-bold tracking-wider uppercase flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
                title="Discard current draft and start over from Step 1"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Start Fresh</span>
              </button>
            </div>
          )}
          <div className="inline-flex items-center justify-center p-2.5 sm:p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200 mb-4">
            <ClipboardList className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight mb-2 px-2">Internal Lead Registration</h1>
          <p className="text-[11px] sm:text-base text-slate-500 font-medium px-4">DOXEZ Surgical CRM • Command Center Flow</p>
        </div>

        {/* Care Pipeline Switcher (Appears once Generator is authenticated or on step >= 2) */}
        {(step >= 2 || validatedGenerator) && (
          <div className="flex justify-center mb-6 sm:mb-8 animate-fadeIn">
            <div className="flex items-center bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/80 shadow-inner gap-1">
              <button
                type="button"
                onClick={() => handleCategorySwitch('appointment')}
                className={`py-2.5 px-4 sm:px-6 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer ${intakeCategory === 'appointment'
                  ? 'bg-[#143E78] text-white shadow-md shadow-blue-900/20'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
              >
                <Calendar className="w-4 h-4 shrink-0" />
                <span>Appointment</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${intakeCategory === 'appointment' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>
                  Minimal
                </span>
              </button>

              <button
                type="button"
                onClick={() => handleCategorySwitch('surgical')}
                className={`py-2.5 px-4 sm:px-6 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer ${intakeCategory === 'surgical'
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
              >
                <Activity className="w-4 h-4 shrink-0" />
                <span>Surgical Care</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${intakeCategory === 'surgical' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  Full Surgery
                </span>
              </button>

              <button
                type="button"
                onClick={() => handleCategorySwitch('ophe')}
                className={`py-2.5 px-4 sm:px-6 rounded-xl font-black text-xs uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer ${intakeCategory === 'ophe'
                  ? 'bg-teal-600 text-white shadow-md shadow-teal-200'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span>OPHE</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${intakeCategory === 'ophe' ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-800'}`}>
                  OPHE Flow
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Stepper / Mode Indicator */}
        {intakeCategory === 'appointment' && step === 2 ? (
          <div className="flex items-center justify-center gap-3 mb-8 sm:mb-12 animate-fadeIn">
            <button
              type="button"
              onClick={() => {
                if (id || isWebLead) {
                  toast.error('Source selection is locked for Website Inquiry leads.');
                } else {
                  setStep(1);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-xs font-black uppercase tracking-wider text-slate-500 hover:bg-slate-50 shadow-xs cursor-pointer"
            >
              <Check className="w-4 h-4 text-emerald-600" />
              <span>1. Source: {validatedGenerator?.name || 'Verified'}</span>
            </button>
            <div className="w-6 h-0.5 bg-blue-300"></div>
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-blue-50 text-[#143E78] border border-blue-200/80 rounded-full text-xs font-black uppercase tracking-wider shadow-xs">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span>2. 1-Step Fast Appointment Intake</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-8 sm:mb-12 relative px-2 sm:px-4">
            <div className="absolute top-[16px] sm:top-1/2 left-0 w-full h-0.5 sm:h-1 bg-slate-200 -translate-y-1/2 z-0"></div>
            <div
              className={`absolute top-[16px] sm:top-1/2 left-0 h-0.5 sm:h-1 bg-indigo-600 -translate-y-1/2 z-0 transition-all duration-500`}
              style={{
                width: intakeCategory === 'ophe'
                  ? `${step === 1 ? 0 : step === 2 ? 33 : step === 3 ? 66 : 100}%`
                  : `${((step - 1) / 4) * 100}%`
              }}
            ></div>

            {[1, 2, 3, 4, 5].map((s) => {
              // Hide Step 2 visually for Marketing (non-web)
              if (s === 2 && validatedGenerator?.type === 'marketing' && !isWebLead) return null;

              // Hide Step 4 (Medical) visually for OPHE without removing it from other pipelines
              if (s === 4 && intakeCategory === 'ophe') return null;

              const isLocked = s === 1 && (!!id || isWebLead);

              return (
                <div
                  key={s}
                  onClick={() => {
                    if (isLocked) {
                      toast.error('Source selection is locked for Website Inquiry leads.');
                    } else if (step > s) {
                      setStep(s);
                    }
                  }}
                  className={`relative z-10 flex flex-col items-center cursor-pointer`}
                  title={isLocked ? 'Source selection is locked for Website Inquiry leads' : ''}
                >
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm transition-all duration-300 ${step >= s ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'bg-white text-slate-400 border-2 border-slate-200'
                    } ${isLocked ? 'opacity-70 border-slate-300' : ''}`}>
                    {step > s ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : s}
                  </div>
                  <span className={`text-[7px] sm:text-[10px] font-black uppercase tracking-[0.2em] mt-2 ${step >= s ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {s === 1 ? 'Source' : s === 2 ? 'Patient' : s === 3 ? 'Intent' : s === 4 ? 'Medical' : 'Confirm'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Form Container */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/60 border border-slate-100 overflow-hidden">

          {/* STEP 1: VALIDATE GENERATOR */}
          {step === 1 && (
            <div className="p-5 sm:p-12 animate-fadeIn">
              <div className="flex items-center gap-4 mb-8 sm:mb-10 pb-6 border-b border-slate-50">
                <div className="p-2.5 sm:p-3 bg-blue-50 rounded-2xl">
                  <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">Identify Lead Generator</h2>
                  <p className="text-[10px] sm:text-xs text-slate-400 font-black uppercase tracking-widest mt-1">Step 1: Partner Authentication</p>
                </div>
              </div>

              <div className="space-y-8">

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4 ml-1">Who provided this lead?</label>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
                    {[
                      { value: 'doctor', label: 'Doctor' },
                      { value: 'hospital', label: 'Hospital' },
                      { value: 'marketing', label: 'Marketing' },
                      { value: 'doxez_lead', label: 'Doxez Lead' },
                      { value: 'ophe', label: 'OPHE' }
                    ].map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          if (value === 'ophe') {
                            setGeneratorType('ophe');
                            setIntakeCategory('ophe');
                            setValidatedGenerator({
                              id: user?._id || 'ophe_partner',
                              name: 'OPHE',
                              type: 'ophe',
                              mobile: '',
                              email: ''
                            });
                            setFormData(prev => ({
                              ...prev,
                              leadType: 'ophe',
                              pipelineChoice: 'ophe'
                            }));
                            setStep(2);
                            return;
                          }
                          setGeneratorType(prev => prev === value ? '' : value);
                        }}
                        className={`py-3 sm:py-4 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-wide transition-all border-2 cursor-pointer ${generatorType === value
                          ? value === 'doxez_lead'
                            ? 'bg-violet-600 border-violet-600 text-white shadow-lg shadow-violet-100'
                            : value === 'ophe'
                              ? 'bg-teal-600 border-teal-600 text-white shadow-lg shadow-teal-100'
                              : 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100'
                          : 'bg-slate-50 border-transparent text-slate-500 hover:bg-slate-100'
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Dedicated Past Surgery Toggle Button */}
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, isManualPastEntry: !prev.isManualPastEntry }))}
                      className={`w-full py-3.5 px-4 rounded-2xl font-black text-xs uppercase tracking-wider transition-all border-2 flex items-center justify-between gap-3 ${formData.isManualPastEntry
                        ? 'bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-100'
                        : 'bg-amber-50/60 border-amber-200 text-amber-800 hover:bg-amber-100/80'
                        }`}
                    >
                      <span className="flex items-center gap-2">
                        <span>⚡</span> Past Completed Surgery (Historical Entry)
                      </span>
                      <span className={`text-[10px] px-2.5 py-1 rounded-lg font-black uppercase ${formData.isManualPastEntry ? 'bg-white/20 text-white' : 'bg-amber-200/60 text-amber-900'
                        }`}>
                        {formData.isManualPastEntry ? 'ENABLED' : 'CLICK TO ENABLE'}
                      </span>
                    </button>
                  </div>
                </div>

                {formData.isManualPastEntry && (
                  <div className="p-4 sm:p-5 bg-amber-50/90 rounded-2xl border border-amber-200 animate-fadeIn">
                    <p className="text-xs sm:text-sm font-black text-amber-900">⚡ Historical Entry Mode Enabled</p>
                    <p className="text-[10px] sm:text-xs text-amber-700 font-semibold mt-0.5">This lead is a completed past surgery. Customer notifications/emails/OTPs and live payment gateway links will be bypassed.</p>
                  </div>
                )}

                {generatorType === 'doxez_lead' ? (
                  <div className="p-5 bg-violet-50 rounded-2xl border-2 border-violet-100 animate-fadeIn">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-violet-600 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-violet-800">Doxez Internal Lead</p>
                        <p className="text-xs text-violet-600 font-semibold mt-0.5">This lead is generated internally by Doxez. No partner verification required.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setValidatedGenerator({ id: user._id, name: 'Doxez Internal', type: 'doxez_lead', mobile: '', email: '' });
                        setStep(2);
                      }}
                      className="mt-4 w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-black rounded-xl transition-all uppercase text-[11px] tracking-widest flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-violet-100"
                    >
                      Proceed as Doxez Lead
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : generatorType === 'ophe' ? null : (
                  <div className="relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4 ml-1">Generator Mobile Number</label>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!validatingGenerator) {
                          handleValidateGenerator();
                        }
                      }}
                      className="flex flex-col sm:flex-row gap-3 sm:gap-4"
                    >
                      <div className="relative flex-1">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                        <input
                          type="tel"
                          value={generatorMobile}
                          onChange={(e) => setGeneratorMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!validatingGenerator) {
                                handleValidateGenerator();
                              }
                            }
                          }}
                          placeholder="10-digit mobile"
                          className="w-full pl-11 pr-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-base sm:text-lg"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={validatingGenerator || generatorMobile.length < 10}
                        className="w-full sm:w-auto px-8 py-4 sm:py-0 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 text-white font-black rounded-xl sm:rounded-2xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-100 uppercase text-[11px] sm:text-sm tracking-widest cursor-pointer"
                      >
                        {validatingGenerator ? 'Searching...' : 'Validate'}
                        <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </form>
                  </div>
                )}

                <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 border-dashed">
                  <p className="text-xs text-slate-500 font-semibold leading-relaxed">
                    <span className="text-indigo-600 font-black">Note:</span> Lead Generator must be registered in the system before you can proceed. If verification fails, ask CC support to register the partner first.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: PATIENT DETAILS (APPOINTMENT MINIMAL OR SURGICAL CORE) */}
          {step === 2 && (
            <div className="p-5 sm:p-12 animate-fadeIn">
              <div className="flex items-center justify-between mb-8 sm:mb-10 pb-6 border-b border-slate-50">
                <div className="flex items-center gap-4">
                  <div className={`p-2.5 sm:p-3 rounded-2xl ${intakeCategory === 'appointment' ? 'bg-blue-50 text-[#143E78]' : intakeCategory === 'ophe' ? 'bg-teal-50 text-teal-600' : 'bg-emerald-50 text-emerald-600'}`}>
                    {intakeCategory === 'appointment' ? (
                      <CalendarClock className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : intakeCategory === 'ophe' ? (
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                      <User className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">
                      {intakeCategory === 'appointment' ? 'Patient Consultation Information' : intakeCategory === 'ophe' ? 'OPHE Patient Identity' : 'Patient Identity'}
                    </h2>
                    <p className="text-[10px] sm:text-xs text-slate-400 font-black uppercase tracking-widest mt-1">
                      {intakeCategory === 'appointment' ? 'Minimal Appointment Intake' : intakeCategory === 'ophe' ? 'Step 2: OPHE Lead Parameters' : 'Step 2: Core Patient Parameters'}
                    </p>
                  </div>
                </div>
                {validatedGenerator && (
                  <div className="hidden md:flex flex-col items-end">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Generated By</span>
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${validatedGenerator.type === 'ophe'
                        ? 'bg-teal-50 border-teal-200'
                        : validatedGenerator.type === 'doxez_lead'
                          ? 'bg-violet-50 border-violet-100'
                          : 'bg-indigo-50 border-indigo-100'
                      }`}>
                      <div className={`w-2 h-2 rounded-full ${validatedGenerator.type === 'ophe'
                          ? 'bg-teal-600'
                          : validatedGenerator.type === 'doxez_lead'
                            ? 'bg-violet-600'
                            : 'bg-indigo-600'
                        } animate-pulse`}></div>
                      <span className={`text-xs font-bold ${validatedGenerator.type === 'ophe'
                          ? 'text-teal-700'
                          : validatedGenerator.type === 'doxez_lead'
                            ? 'text-violet-700'
                            : 'text-indigo-700'
                        }`}>{validatedGenerator.name}</span>
                    </div>
                    {(validatedGenerator.mobile || validatedGenerator.email) && (
                      <div className="flex items-center gap-3 pr-1 mt-1">
                        {validatedGenerator.mobile && (
                          <span className="text-[10px] font-bold text-slate-400 font-mono">{validatedGenerator.mobile}</span>
                        )}
                        {validatedGenerator.mobile && validatedGenerator.email && (
                          <span className="text-[10px] font-bold text-slate-300">•</span>
                        )}
                        {validatedGenerator.email && (
                          <span className="text-[10px] font-bold text-slate-400 truncate max-w-[150px]">{validatedGenerator.email}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
                {/* 1️⃣ Mobile Number — Required (FIRST) */}
                <div className="space-y-1.5" ref={fieldRefs.patientPhone}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-widest block ml-1" style={{ color: fieldErrors.patientPhone ? '#ef4444' : '' }}>
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    {isSearchingPhone && (
                      <span className="text-[10px] font-bold text-indigo-600 flex items-center gap-1 animate-pulse">
                        <RotateCcw size={10} className="animate-spin" /> Checking history...
                      </span>
                    )}
                    {!isSearchingPhone && lookupData && lookupData.patients && lookupData.patients.length > 0 && (
                      <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Users size={11} /> {lookupData.patients.length} Patient{lookupData.patients.length > 1 ? 's' : ''}
                      </span>
                    )}
                    {!isSearchingPhone && lookupData && lookupData.patients && lookupData.patients.length === 0 && (
                      <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Sparkles size={11} /> New Number
                      </span>
                    )}
                  </div>
                  <div className="relative flex items-center">
                    <span className="absolute left-3.5 sm:left-4 text-xs font-black text-slate-400 select-none pointer-events-none">
                      +91
                    </span>
                    <input
                      type="tel"
                      name="patientPhone"
                      value={formData.patientPhone}
                      onChange={(e) => { handleInputChange(e); setFieldErrors(p => ({ ...p, patientPhone: '' })); }}
                      placeholder="10-digit mobile"
                      maxLength={10}
                      className={`w-full pl-12 sm:pl-14 pr-10 py-3.5 sm:py-4 bg-slate-50 border-2 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-mono font-bold transition-all text-sm sm:text-base ${fieldErrors.patientPhone
                        ? 'border-red-400 bg-red-50 focus:border-red-500'
                        : selectedPatientProfile
                          ? 'border-indigo-500 bg-white ring-2 ring-indigo-500/10'
                          : 'border-transparent focus:border-indigo-600'
                        }`}
                    />
                    <Phone size={16} className={`absolute right-4 pointer-events-none transition-colors ${selectedPatientProfile ? 'text-indigo-600' : 'text-slate-400'
                      }`} />
                  </div>
                  {fieldErrors.patientPhone && <p className="text-[10px] text-red-500 font-bold ml-1 mt-1">⚠ {fieldErrors.patientPhone}</p>}
                </div>

                {/* 2️⃣ Patient Full Name — Required (WITH INLINE DROPDOWN) */}
                <div className="space-y-1.5 relative" ref={nameDropdownRef}>
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-widest block ml-1" style={{ color: fieldErrors.patientName ? '#ef4444' : '' }}>
                      Patient Full Name <span className="text-red-500">*</span>
                    </label>
                    {selectedPatientProfile && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Check size={10} /> Auto-filled
                        </span>
                        <button
                          type="button"
                          onClick={handleResetSelection}
                          className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 underline cursor-pointer"
                        >
                          Change
                        </button>
                      </div>
                    )}
                    {isNewPatientUnderSamePhone && (
                      <span className="text-[10px] font-black text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <UserPlus size={10} /> New Patient
                      </span>
                    )}
                  </div>
                  <div className="relative" ref={fieldRefs.patientName}>
                    <input
                      type="text"
                      name="patientName"
                      value={formData.patientName}
                      onChange={(e) => {
                        handleInputChange(e);
                        setFieldErrors(p => ({ ...p, patientName: '' }));
                        if (lookupData?.patients?.length > 0 && !isNameDropdownOpen) {
                          setIsNameDropdownOpen(true);
                        }
                      }}
                      onFocus={() => {
                        if (lookupData?.patients?.length > 0) {
                          setIsNameDropdownOpen(true);
                        }
                      }}
                      placeholder={
                        lookupData?.patients?.length > 0
                          ? `Select existing (${lookupData.patients.length}) or type name...`
                          : "Full name as per ID"
                      }
                      className={`w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base ${fieldErrors.patientName
                        ? 'border-red-400 bg-red-50 focus:border-red-500'
                        : selectedPatientProfile
                          ? 'border-indigo-400 bg-white ring-2 ring-indigo-500/10'
                          : 'border-transparent focus:border-indigo-600'
                        }`}
                    />
                    {lookupData?.patients?.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setIsNameDropdownOpen(prev => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                        title="View patients registered under this number"
                      >
                        <ChevronDown size={17} className={`transition-transform duration-200 ${isNameDropdownOpen ? 'rotate-180 text-indigo-600' : ''}`} />
                      </button>
                    )}
                  </div>
                  {fieldErrors.patientName && <p className="text-[10px] text-red-500 font-bold ml-1 mt-1">⚠ {fieldErrors.patientName}</p>}

                  {/* 📋 INLINE FLOATING NAME DROPDOWN */}
                  {isNameDropdownOpen && lookupData?.patients && lookupData.patients.length > 0 && (
                    <div className="absolute z-40 top-full left-0 right-0 mt-2 bg-white rounded-2xl border-2 border-indigo-100 shadow-2xl shadow-indigo-950/15 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                      <div className="p-2.5 bg-indigo-50/70 border-b border-indigo-100 flex items-center justify-between text-[11px] font-black text-indigo-900">
                        <span className="flex items-center gap-1.5">
                          <Users size={13} className="text-indigo-600" />
                          <span>Patients found for +91 {lookupData.phone}</span>
                        </span>
                        <span className="text-[10px] font-bold text-indigo-600 bg-white px-2 py-0.5 rounded-full border border-indigo-200">
                          {lookupData.patients.length} Profile{lookupData.patients.length > 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-1.5">
                        {lookupData.patients.map((patient, idx) => {
                          const isSelected = selectedPatientProfile && selectedPatientProfile.patientName.toLowerCase() === patient.patientName.toLowerCase();
                          const initials = patient.patientName.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleSelectPatient(patient)}
                              className={`w-full text-left p-3 rounded-xl transition-all flex items-start justify-between gap-3 cursor-pointer ${isSelected
                                ? 'bg-indigo-50/90 border border-indigo-200'
                                : 'hover:bg-slate-50'
                                }`}
                            >
                              <div className="flex items-start gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs shrink-0 mt-0.5 ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700'
                                  }`}>
                                  {initials || 'PT'}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-black text-slate-900 truncate">
                                    {patient.patientName}
                                  </p>
                                  <p className="text-[11px] text-slate-500 font-medium truncate mt-0.5">
                                    {patient.patientAge ? `${patient.patientAge} Yrs` : 'Age N/A'} • {patient.patientGender === 'female' ? 'Female' : 'Male'}
                                    {patient.bloodGroup ? ` • ${patient.bloodGroup}` : ''}
                                    {patient.city ? ` • ${patient.city}` : ''}
                                  </p>
                                  {patient.treatmentRequired && (
                                    <p className="text-[10px] text-indigo-600 font-bold truncate mt-0.5">
                                      Prev: {patient.treatmentRequired} ({patient.totalRecords} visit{patient.totalRecords > 1 ? 's' : ''})
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isSelected ? (
                                <span className="px-2 py-0.5 bg-emerald-500 text-white text-[9px] font-black rounded-md uppercase shrink-0 mt-1">
                                  ✓ Active
                                </span>
                              ) : (
                                <span className="text-[10px] font-bold text-indigo-600 hover:underline shrink-0 mt-1">
                                  Auto-fill →
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      <div className="p-2 bg-slate-50 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={handleAddNewPatientUnderSamePhone}
                          className="w-full py-2 px-3 rounded-xl bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/50 text-indigo-700 text-xs font-black transition-all flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <UserPlus size={14} />
                          <span>+ Add a Different Person / Family Member</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Email — Optional */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Patient Email</label>
                  <input
                    type="email"
                    name="patientEmail"
                    value={formData.patientEmail}
                    onChange={handleInputChange}
                    placeholder="patient@example.com"
                    className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base"
                  />
                </div>

                {/* Blood Group — Optional */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Blood Group</label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base"
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                {/* Date of Birth — Optional (Hidden for OPHE) */}
                {intakeCategory !== 'ophe' && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      max={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base"
                    />
                  </div>
                )}

                {/* Emergency Contact — Only in Surgical/OPHE Mode */}
                {(intakeCategory === 'surgical' || intakeCategory === 'ophe') && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Emergency Contact</label>
                    <input
                      type="tel"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                      placeholder="10-digit emergency number"
                      maxLength={10}
                      className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-mono font-bold transition-all text-sm sm:text-base"
                    />
                  </div>
                )}

                {/* ABHA ID — Only in Surgical Mode (Hidden for OPHE) */}
                {intakeCategory === 'surgical' && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">ABHA ID (Optional)</label>
                    <input
                      type="text"
                      name="abhaId"
                      value={formData.abhaId}
                      onChange={handleInputChange}
                      placeholder="e.g. 1234-5678-9012"
                      className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base"
                    />
                  </div>
                )}

                {/* Age — Required */}
                <div className="space-y-1.5" ref={fieldRefs.patientAge}>
                  <label className="text-[10px] font-black uppercase tracking-widest block ml-1" style={{ color: fieldErrors.patientAge ? '#ef4444' : '' }}>
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="patientAge"
                    min={0}
                    max={125}
                    onKeyDown={(e) => {
                      // Block negative sign (-), positive (+), and scientific notation (e, E)
                      if (['-', '+', 'e', 'E'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    value={formData.patientAge}
                    onChange={(e) => { handleInputChange(e); setFieldErrors(p => ({ ...p, patientAge: '' })); }}
                    placeholder="e.g. 45"
                    className={`w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base ${fieldErrors.patientAge ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-transparent focus:border-indigo-600'
                      }`}
                  />
                  {fieldErrors.patientAge && <p className="text-[10px] text-red-500 font-bold ml-1 mt-1">⚠ {fieldErrors.patientAge}</p>}
                </div>

                {/* Gender — Optional */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Gender</label>
                  <select
                    name="patientGender"
                    value={formData.patientGender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Treatment Required / Chief Complaints — Required (Hidden for OPHE) */}
                {intakeCategory !== 'ophe' && (
                  <div className="space-y-1.5" ref={fieldRefs.treatmentRequired}>
                    <label className="text-[10px] font-black uppercase tracking-widest block ml-1" style={{ color: fieldErrors.treatmentRequired ? '#ef4444' : '' }}>
                      {intakeCategory === 'appointment' ? 'Consultation Reason / Chief Complaints' : 'Treatment Required'} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="treatmentRequired"
                      value={formData.treatmentRequired}
                      onChange={(e) => { handleInputChange(e); setFieldErrors(p => ({ ...p, treatmentRequired: '' })); }}
                      placeholder={intakeCategory === 'appointment' ? 'e.g. Doctor Consultation, Knee Pain, Fever, ENT Issue' : 'e.g. Laparoscopic Cholecystectomy'}
                      className={`w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all text-sm sm:text-base ${fieldErrors.treatmentRequired ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-transparent focus:border-indigo-600'
                        }`}
                    />
                    {fieldErrors.treatmentRequired && <p className="text-[10px] text-red-500 font-bold ml-1 mt-1">⚠ {fieldErrors.treatmentRequired}</p>}
                  </div>
                )}

                {/* Comorbidities — Optional (Hidden for OPHE) */}
                {intakeCategory !== 'ophe' && (
                  <div className="md:col-span-2 space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block ml-1">Comorbidities (Any Other Chronic Illness)</label>
                    <textarea
                      name="comorbidities"
                      value={formData.comorbidities}
                      onChange={handleInputChange}
                      rows="2"
                      placeholder="e.g. BP, Diabetes, Asthma, Thyroid, Heart Disease"
                      className="w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all resize-none text-sm sm:text-base"
                    ></textarea>
                  </div>
                )}

                {/* Address — Required */}
                <div className="md:col-span-2 space-y-1.5" ref={fieldRefs.patientAddress}>
                  <label className="text-[10px] font-black uppercase tracking-widest block ml-1" style={{ color: fieldErrors.patientAddress ? '#ef4444' : '' }}>
                    Detailed Address / City <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="patientAddress"
                    value={formData.patientAddress}
                    onChange={(e) => { handleInputChange(e); setFieldErrors(p => ({ ...p, patientAddress: '' })); }}
                    rows="3"
                    placeholder="Complete address for clinical matching"
                    className={`w-full px-4 py-3.5 sm:py-4 bg-slate-50 border-2 focus:bg-white rounded-xl sm:rounded-2xl outline-none font-bold transition-all resize-none text-sm sm:text-base ${fieldErrors.patientAddress ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-transparent focus:border-indigo-600'
                      }`}
                  ></textarea>
                  {fieldErrors.patientAddress && <p className="text-[10px] text-red-500 font-bold ml-1 mt-1">⚠ {fieldErrors.patientAddress}</p>}
                </div>

                {/* 🩺 OPHE EXCLUSIVE: Care Service & Package Selection */}
                {intakeCategory === 'ophe' && (
                  <div ref={fieldRefs.opheService} className="md:col-span-2 p-5 sm:p-7 rounded-[2rem] bg-gradient-to-br from-teal-50/70 via-white to-slate-50 border-2 border-teal-200/90 shadow-sm space-y-6 animate-fadeIn">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-teal-100 gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-teal-600 text-white flex items-center justify-center font-black shadow-md shadow-teal-200">
                          <HeartHandshake className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">OPHE Care Service &amp; Package</h3>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-600 text-white">Required</span>
                          </div>
                          <p className="text-[11px] font-bold text-slate-400 mt-0.5">Select the care category, service package &amp; duration for this patient</p>
                        </div>
                      </div>

                      {formData.opheServiceDetails?.serviceName && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-black self-start sm:self-auto shadow-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="truncate max-w-[180px]">{formData.opheServiceDetails.serviceName}</span>
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded-full uppercase">
                            {formData.opheServiceDetails.planDuration === 'perDay' ? '1 Day' :
                              formData.opheServiceDetails.planDuration === 'days7' ? '7 Days' :
                                formData.opheServiceDetails.planDuration === 'days15' ? '15 Days' : '30 Days'}
                          </span>
                        </div>
                      )}
                    </div>

                    {fieldErrors.opheService && (
                      <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs font-bold text-red-600 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                        <span>{fieldErrors.opheService}</span>
                      </div>
                    )}

                    {/* 1. Category Filter Tabs */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">1. Care Category Filter</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedOpheCategoryFilter('all')}
                          className={`px-3 py-2 rounded-xl text-xs font-black transition-all cursor-pointer ${selectedOpheCategoryFilter === 'all'
                              ? 'bg-teal-700 text-white shadow-md shadow-teal-700/20'
                              : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                            }`}
                        >
                          All Categories ({opheServices.length})
                        </button>
                        {opheCategories.map(cat => {
                          const count = opheServices.filter(s => (s.categoryId?._id || s.categoryId) === cat._id).length;
                          const isSelected = selectedOpheCategoryFilter === cat._id;
                          return (
                            <button
                              key={cat._id}
                              type="button"
                              onClick={() => setSelectedOpheCategoryFilter(cat._id)}
                              className={`px-3 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer ${isSelected
                                  ? 'bg-teal-700 text-white shadow-md shadow-teal-700/20'
                                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                                }`}
                            >
                              {cat.imageUrl && (
                                <img src={cat.imageUrl} alt={cat.name} className="w-4 h-4 rounded-full object-cover shrink-0" />
                              )}
                              <span className="capitalize">{cat.name}</span>
                              <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* 2. Service Packages Grid */}
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">2. Choose Service Package *</label>
                      {loadingOpheCatalog ? (
                        <div className="p-6 text-center text-xs text-slate-400 font-bold bg-white rounded-2xl border border-slate-200">
                          Loading OPHE service catalog...
                        </div>
                      ) : opheServices.length === 0 ? (
                        <div className="p-6 text-center text-xs text-amber-800 bg-amber-50 rounded-2xl border border-amber-200 font-bold">
                          No active OPHE services found in the catalog. Please configure services in the OPHE Services module.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {opheServices
                            .filter(s => selectedOpheCategoryFilter === 'all' || (s.categoryId?._id || s.categoryId) === selectedOpheCategoryFilter)
                            .map(serv => {
                              const isSelected = formData.opheServiceDetails?.serviceId === serv._id;
                              const catObj = opheCategories.find(c => c._id === (serv.categoryId?._id || serv.categoryId)) || serv.categoryId;
                              return (
                                <div
                                  key={serv._id}
                                  onClick={() => handleSelectOpheService(serv, formData.opheServiceDetails?.planDuration || 'perDay')}
                                  className={`rounded-2xl border-2 transition-all cursor-pointer text-left relative overflow-hidden flex flex-col justify-between ${isSelected
                                      ? 'border-teal-500 bg-white shadow-lg ring-2 ring-teal-500/20'
                                      : 'border-slate-200/90 bg-white hover:border-teal-300 hover:shadow-md'
                                    }`}
                                >
                                  {/* Top Header & Image */}
                                  <div className="p-4 space-y-3">
                                    <div className="flex items-start gap-3">
                                      {serv.imageUrl ? (
                                        <img src={serv.imageUrl} alt={serv.serviceName} className="w-14 h-14 rounded-2xl object-cover shrink-0 border border-slate-100 shadow-2xs" />
                                      ) : (
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-2xs ${isSelected ? 'bg-teal-700 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                          <HeartHandshake className="w-7 h-7" />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-teal-50 text-teal-800 border border-teal-200/60 rounded-md">
                                            {catObj?.name || 'Care Package'}
                                          </span>
                                          {isSelected && (
                                            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-emerald-600 text-white rounded-md flex items-center gap-1 shadow-xs">
                                              <Check className="w-3 h-3" /> Selected
                                            </span>
                                          )}
                                        </div>
                                        <h4 className="text-sm font-black text-slate-900 mt-1 leading-tight">{serv.serviceName}</h4>
                                        {serv.description && (
                                          <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-snug">{serv.description}</p>
                                        )}
                                      </div>
                                    </div>

                                    {/* 🩺 Medical Inclusions */}
                                    {serv.medicalPoints && serv.medicalPoints.length > 0 && (
                                      <div className="bg-emerald-50/50 p-2.5 rounded-xl border border-emerald-100/70 space-y-1">
                                        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                                          <Stethoscope className="w-3 h-3 text-emerald-600" />
                                          Medical Inclusions ({serv.medicalPoints.length})
                                        </span>
                                        <ul className="space-y-0.5">
                                          {serv.medicalPoints.slice(0, 2).map((pt, idx) => (
                                            <li key={idx} className="text-[11px] text-slate-700 flex items-start gap-1.5 font-medium leading-tight">
                                              <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" />
                                              <span className="line-clamp-1">{pt}</span>
                                            </li>
                                          ))}
                                          {serv.medicalPoints.length > 2 && (
                                            <li className="text-[10px] text-emerald-700 font-bold italic pl-4.5">
                                              +{serv.medicalPoints.length - 2} more medical points
                                            </li>
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                    {/* 💜 Non-Medical Inclusions */}
                                    {serv.nonMedicalPoints && serv.nonMedicalPoints.length > 0 && (
                                      <div className="bg-violet-50/50 p-2.5 rounded-xl border border-violet-100/70 space-y-1">
                                        <span className="text-[10px] font-black uppercase tracking-wider text-violet-800 flex items-center gap-1">
                                          <HeartHandshake className="w-3 h-3 text-violet-600" />
                                          Non-Medical Care ({serv.nonMedicalPoints.length})
                                        </span>
                                        <ul className="space-y-0.5">
                                          {serv.nonMedicalPoints.slice(0, 2).map((pt, idx) => (
                                            <li key={idx} className="text-[11px] text-slate-700 flex items-start gap-1.5 font-medium leading-tight">
                                              <CheckCircle2 className="w-3 h-3 text-violet-600 shrink-0 mt-0.5" />
                                              <span className="line-clamp-1">{pt}</span>
                                            </li>
                                          ))}
                                          {serv.nonMedicalPoints.length > 2 && (
                                            <li className="text-[10px] text-violet-700 font-bold italic pl-4.5">
                                              +{serv.nonMedicalPoints.length - 2} more care points
                                            </li>
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </div>

                                  {/* 💰 Full Pricing Breakdown Ribbon */}
                                  <div className="bg-slate-50/90 p-3 border-t border-slate-100 space-y-2">
                                    {/* Shift Rates */}
                                    {(serv.pricing?.hours12 || serv.pricing?.hours24 || serv.pricing?.pricingMode === 'hourly_shift') && (
                                      <div className="grid grid-cols-3 gap-1.5 text-center">
                                        <div className="bg-white p-1.5 rounded-lg border border-teal-200/70">
                                          <span className="text-[9px] text-teal-700 font-bold block">12h Shift</span>
                                          <span className="text-xs font-black text-slate-900">
                                            ₹{(serv.pricing?.hours12 || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                        <div className="bg-white p-1.5 rounded-lg border border-indigo-200/70">
                                          <span className="text-[9px] text-indigo-700 font-bold block">24h Shift</span>
                                          <span className="text-xs font-black text-indigo-950">
                                            ₹{(serv.pricing?.hours24 || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                        <div className="bg-white p-1.5 rounded-lg border border-slate-200/80">
                                          <span className="text-[9px] text-slate-500 font-bold block">Custom/Hr</span>
                                          <span className="text-xs font-black text-slate-800">
                                            ₹{(serv.pricing?.customHoursRate || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                      </div>
                                    )}

                                    {/* Multi-Day Rates */}
                                    {(serv.pricing?.perDay || serv.pricing?.days7 || serv.pricing?.pricingMode !== 'hourly_shift') && (
                                      <div className="grid grid-cols-4 gap-1 text-center">
                                        <div className="bg-white p-1 rounded-lg border border-slate-200">
                                          <span className="text-[9px] text-slate-500 font-bold block">Per Day</span>
                                          <span className="text-[11px] font-black text-slate-900">
                                            ₹{(serv.pricing?.perDay || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                        <div className="bg-white p-1 rounded-lg border border-slate-200">
                                          <span className="text-[9px] text-slate-500 font-bold block">7 Days</span>
                                          <span className="text-[11px] font-black text-teal-800">
                                            ₹{(serv.pricing?.days7 || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                        <div className="bg-white p-1 rounded-lg border border-slate-200">
                                          <span className="text-[9px] text-slate-500 font-bold block">15 Days</span>
                                          <span className="text-[11px] font-black text-blue-800">
                                            ₹{(serv.pricing?.days15 || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                        <div className="bg-white p-1 rounded-lg border border-slate-200">
                                          <span className="text-[9px] text-slate-500 font-bold block">30 Days</span>
                                          <span className="text-[11px] font-black text-emerald-800">
                                            ₹{(serv.pricing?.days30 || 0).toLocaleString('en-IN')}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 🏥 OPHE EXCLUSIVE: Medical Assessment & Condition Details */}
                {intakeCategory === 'ophe' && (
                  <div className="md:col-span-2 mt-4 p-5 sm:p-8 rounded-[2rem] bg-gradient-to-br from-indigo-50/70 via-white to-slate-50 border-2 border-indigo-200/80 shadow-sm space-y-6 animate-fadeIn">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-indigo-100 gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black shadow-md shadow-indigo-200">
                          <HeartPulse className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">OPHE Medical Assessment</h3>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-600 text-white">OPHE Clinical</span>
                          </div>
                          <p className="text-[11px] font-bold text-slate-400 mt-0.5">Required clinical details & vitals for OPHE patient intake</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                      {/* 1. Diabetes / Sugar */}
                      <div className="p-4 sm:p-5 bg-white rounded-2xl border border-indigo-100 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                            <Droplet className="w-3.5 h-3.5 text-rose-500" />
                            Diabetes / Sugar
                          </label>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Yes / No</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {['No', 'Yes'].map(ans => (
                            <button
                              key={ans}
                              type="button"
                              onClick={() => handleOpheMedicalChange('diabetesStatus', formData.opheMedicalInfo?.diabetesStatus === ans ? '' : ans)}
                              className={`py-2.5 px-3 rounded-xl text-xs font-black uppercase transition-all ${formData.opheMedicalInfo?.diabetesStatus === ans
                                  ? ans === 'Yes' ? 'bg-rose-600 text-white shadow-md shadow-rose-200' : 'bg-slate-800 text-white shadow-md'
                                  : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                                }`}
                            >
                              {ans}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 2. Blood Pressure (BP) */}
                      <div className="p-4 sm:p-5 bg-white rounded-2xl border border-indigo-100 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                            <Activity className="w-3.5 h-3.5 text-blue-500" />
                            Blood Pressure (BP)
                          </label>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Yes / No</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {['No', 'Yes'].map(ans => (
                            <button
                              key={ans}
                              type="button"
                              onClick={() => handleOpheMedicalChange('bpStatus', formData.opheMedicalInfo?.bpStatus === ans ? '' : ans)}
                              className={`py-2.5 px-3 rounded-xl text-xs font-black uppercase transition-all ${formData.opheMedicalInfo?.bpStatus === ans
                                  ? ans === 'Yes' ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-slate-800 text-white shadow-md'
                                  : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                                }`}
                            >
                              {ans}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* 3. Thyroid */}
                      <div className="p-4 sm:p-5 bg-white rounded-2xl border border-indigo-100 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                            Thyroid
                          </label>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Yes / No; Type & Value</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {['No', 'Yes'].map(ans => (
                            <button
                              key={ans}
                              type="button"
                              onClick={() => {
                                handleOpheMedicalChange('thyroidStatus', ans);
                                if (ans === 'No') {
                                  handleOpheMedicalChange('thyroidType', '');
                                  handleOpheMedicalChange('thyroidValueReport', '');
                                }
                              }}
                              className={`py-2 px-3 rounded-xl text-xs font-black uppercase transition-all ${formData.opheMedicalInfo?.thyroidStatus === ans
                                  ? ans === 'Yes' ? 'bg-violet-600 text-white shadow-md shadow-violet-200' : 'bg-slate-800 text-white'
                                  : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                                }`}
                            >
                              {ans}
                            </button>
                          ))}
                        </div>
                        {formData.opheMedicalInfo?.thyroidStatus === 'Yes' && (
                          <div className="space-y-2 pt-1 animate-fadeIn">
                            <div className="grid grid-cols-2 gap-2">
                              {['Hypothyroid', 'Hyperthyroid'].map(type => (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => handleOpheMedicalChange('thyroidType', formData.opheMedicalInfo?.thyroidType === type ? '' : type)}
                                  className={`py-1.5 px-2 rounded-lg text-[11px] font-black uppercase transition-all ${formData.opheMedicalInfo?.thyroidType === type
                                      ? 'bg-violet-100 text-violet-800 border-2 border-violet-400 font-black'
                                      : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'
                                    }`}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                            <input
                              type="text"
                              placeholder="Relevant value / report details (e.g. TSH: 6.5 uIU/mL)"
                              value={formData.opheMedicalInfo?.thyroidValueReport || ''}
                              onChange={(e) => handleOpheMedicalChange('thyroidValueReport', e.target.value)}
                              className="w-full px-4 py-2.5 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold text-xs sm:text-sm transition-all"
                            />
                          </div>
                        )}
                      </div>

                      {/* 4. Other Medical Conditions */}
                      <div className="p-4 sm:p-5 bg-white rounded-2xl border border-indigo-100 shadow-sm space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                            Other Medical Conditions
                          </label>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Condition Details</span>
                        </div>
                        <textarea
                          rows="3"
                          placeholder="Condition details (e.g. Asthma, Cardiac history, Allergies, Previous surgeries)"
                          value={formData.opheMedicalInfo?.otherConditions || ''}
                          onChange={(e) => handleOpheMedicalChange('otherConditions', e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold text-xs sm:text-sm transition-all resize-none"
                        ></textarea>
                      </div>

                      {/* 5. Current Medication */}
                      <div className="p-4 sm:p-5 bg-white rounded-2xl border border-indigo-100 shadow-sm space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                            <Pill className="w-3.5 h-3.5 text-emerald-500" />
                            Current Medication
                          </label>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Medication Details</span>
                        </div>
                        <textarea
                          rows="3"
                          placeholder="Current medication information (e.g. Metformin 500mg, Telmisartan 40mg, Insulin)"
                          value={formData.opheMedicalInfo?.currentMedication || ''}
                          onChange={(e) => handleOpheMedicalChange('currentMedication', e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 border-2 border-transparent focus:border-indigo-600 focus:bg-white rounded-xl outline-none font-bold text-xs sm:text-sm transition-all resize-none"
                        ></textarea>
                      </div>

                      {/* 6. Medical Reports / Documents */}
                      <div className="p-4 sm:p-5 bg-white rounded-2xl border border-indigo-100 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-cyan-600" />
                            Medical Reports / Documents
                          </label>
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Uploaded Reports</span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-3">
                          <input
                            type="file"
                            multiple
                            id="ophe-reports-upload"
                            className="hidden"
                            onChange={(e) => handleFileUpload(Array.from(e.target.files), 'medical')}
                          />
                          <label
                            htmlFor="ophe-reports-upload"
                            className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-sm cursor-pointer transition-all"
                          >
                            <Upload className="w-4 h-4" />
                            Upload Reports ({formData.medicalReports?.length || 0})
                          </label>
                          <p className="text-[10px] font-medium text-slate-400 text-center sm:text-left leading-tight">
                            Prescriptions, lab results, ultrasound, or medical records.
                          </p>
                        </div>

                        {formData.medicalReports && formData.medicalReports.length > 0 && (
                          <div className="grid grid-cols-1 gap-2 pt-1 max-h-32 overflow-y-auto pr-1">
                            {formData.medicalReports.map((report, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-xl border border-slate-200/80">
                                <div className="flex items-center gap-2 truncate">
                                  <FileText className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                                  <span className="text-xs font-bold text-slate-700 truncate" title={report.name}>
                                    {report.name || `Report ${idx + 1}`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                  <a
                                    href={report.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                                    title="View report"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const up = [...formData.medicalReports];
                                      up.splice(idx, 1);
                                      setFormData({ ...formData, medicalReports: up });
                                    }}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                    title="Remove report"
                                  >
                                    <XCircle className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row justify-between gap-3 mt-10 sm:mt-12">
                <button
                  onClick={() => {
                    if (id || isWebLead) {
                      toast.error('Source selection is locked.');
                    } else {
                      setStep(1);
                    }
                  }}
                  className={`order-2 sm:order-1 px-8 py-4 bg-white border-2 border-slate-200 font-black rounded-xl sm:rounded-2xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest ${(id || isWebLead) ? 'text-slate-400 cursor-not-allowed opacity-75' : 'text-slate-600'}`}
                  title={(id || isWebLead) ? 'Source selection is locked' : 'Back to Source'}
                >
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" /> Back to Source
                </button>

                {intakeCategory === 'appointment' ? (
                  <button
                    onClick={handleAppointmentSubmit}
                    disabled={loading}
                    className="order-1 sm:order-2 px-10 py-4 bg-[#143E78] hover:bg-[#0f2e5a] text-white font-black rounded-xl sm:rounded-2xl shadow-xl shadow-blue-900/20 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest disabled:opacity-50 cursor-pointer"
                  >
                    {loading ? (
                      'Creating Appointment Lead...'
                    ) : (
                      <>
                        Create Appointment Lead <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handlePatientContinue}
                    className="order-1 sm:order-2 px-10 py-4 bg-indigo-600 text-white font-black rounded-xl sm:rounded-2xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest cursor-pointer"
                  >
                    Continue <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: STRATEGY & INTENT (Restored) */}
          {step === 3 && (
            <div className="p-5 sm:p-12 animate-fadeIn flex-1">
              <div className="flex items-center gap-4 mb-8 sm:mb-10 pb-6 border-b border-slate-50">
                <div className="p-2.5 sm:p-3 bg-indigo-50 rounded-2xl">
                  <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">Strategy & Intent Alignment</h2>
                  <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Protocol Phase 3 for {validatedGenerator?.name}</p>
                </div>
              </div>

              <div className="space-y-12">
                {/* DOCTOR SPECIFIC: HOSPITAL PICKER */}
                {(validatedGenerator?.type === 'doctor' || validatedGenerator?.type === 'admin') && (
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Target Operating Facility (Doctor's Choice)</label>
                    <SearchableSelect
                      options={hospitals}
                      value={formData.assignedHospital}
                      onChange={(id) => setFormData({ ...formData, assignedHospital: id })}
                      label="Select In-panel Hospital"
                      type="hospital"
                      placeholder="Search facility name..."
                    />
                  </div>
                )}

                {/* HOSPITAL SPECIFIC: INFO */}
                {validatedGenerator?.type === 'hospital' && (
                  <div className="p-5 sm:p-8 bg-emerald-50 rounded-[2rem] sm:rounded-[2.5rem] border border-emerald-100 flex items-center gap-4 sm:gap-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-2xl sm:rounded-3xl shadow-sm flex items-center justify-center shrink-0">
                      <Building className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[9px] sm:text-[10px] font-black text-emerald-400 uppercase tracking-widest">Facility Ownership Detect</p>
                      <h4 className="text-lg sm:text-xl font-black text-slate-800 uppercase tracking-tight leading-tight">{validatedGenerator?.name || 'Assigned Partner'}</h4>
                      <p className="text-[10px] sm:text-xs font-bold text-slate-400">Surgical operations audited for this facility.</p>
                    </div>
                  </div>
                )}

                {/* INTENT CHOICE: ONLY LEAD VS SURGERY */}
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4 ml-1">Engagement Intent (As per Partner)</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {[
                      {
                        id: 'lead_only',
                        label: validatedGenerator?.type === 'hospital' ? 'Providing Lead Only' : 'Referring - Lead Only',
                        desc: validatedGenerator?.type === 'hospital' ? 'Referral mode. Treatment elsewhere.' : 'Sharing case. DOXEZ handles facility.',
                        icon: Activity
                      },
                      {
                        id: 'surgery',
                        label: validatedGenerator?.type === 'doctor' ? 'Performing Surgery' : 'Operating in our Hospital',
                        desc: validatedGenerator?.type === 'doctor' ? 'Partner is the primary surgeon.' : 'Surgical procedure in-house.',
                        icon: Stethoscope
                      }
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setFormData({ ...formData, leadType: formData.leadType === type.id ? '' : type.id })}
                        className={`p-5 sm:p-6 rounded-[1.5rem] sm:rounded-3xl border-2 transition-all flex items-start gap-4 text-left ${formData.leadType === type.id
                          ? 'border-indigo-600 bg-indigo-50 ring-4 ring-indigo-50 shadow-xl'
                          : 'border-slate-100 bg-white hover:border-slate-200'
                          }`}
                      >
                        <div className={`p-2.5 sm:p-3 rounded-2xl ${formData.leadType === type.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                          <type.icon className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                          <p className={`font-black uppercase tracking-widest text-[10px] sm:text-[11px] mb-1 ${formData.leadType === type.id ? 'text-indigo-700' : 'text-slate-700'}`}>{type.label}</p>
                          <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 leading-relaxed">{type.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between gap-3 mt-10 sm:mt-12 pt-8 border-t border-slate-50">
                <button
                  onClick={() => setStep(2)}
                  className="order-2 sm:order-1 px-8 py-4 text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to Patient
                </button>
                <button
                  onClick={() => {
                    if (validatedGenerator?.type === 'doctor' && formData.leadType === 'surgery' && !formData.assignedHospital) {
                      return toast.error('Selection Required: Please select the operating hospital for this surgery.');
                    }
                    if (!formData.leadType) {
                      return toast.error('Strategy Missing: Please select the partner intent.');
                    }
                    const hasDocs = (remoteLead?.documents?.length > 0) || (formData?.medicalReports?.length > 0);
                    syncProgress(hasDocs ? 5 : 4);
                  }}
                  className="order-1 sm:order-2 px-10 sm:px-12 py-4 bg-indigo-600 text-white font-black rounded-xl sm:rounded-2xl shadow-xl shadow-indigo-100 hover:scale-[1.02] flex items-center justify-center gap-3 transition-all text-xs sm:text-sm uppercase tracking-widest"
                >
                  {((remoteLead?.documents?.length > 0) || (formData?.medicalReports?.length > 0)) ? 'Continue to Audit' : 'Continue to Protocols'} <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: MEDICAL & AYUSHMAN */}
          {step === 4 && (
            <div className="p-5 sm:p-12 animate-fadeIn">
              <div className="flex items-center gap-4 mb-8 sm:mb-10 pb-6 border-b border-slate-50">
                <div className="p-2.5 sm:p-3 bg-indigo-50 rounded-2xl">
                  <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">Medical & Eligibility Documents</h2>
                  <p className="text-[10px] sm:text-xs text-slate-400 font-black uppercase tracking-widest mt-1">Step 4: Upload Reports & ID Proofs</p>
                </div>
              </div>

              <div className="space-y-10">
                {/* Eligibility Mode Selection — Optional */}
                <div ref={fieldRefs.eligibilityMode}>
                  <label
                    className="text-[10px] font-black uppercase tracking-widest block mb-6 ml-1 text-center text-slate-500"
                  >
                    Select Eligibility Mode <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 rounded-3xl transition-all">
                    {[
                      { id: 'ayushman', label: 'Ayushman Card', icon: CreditCard, color: 'indigo' },
                      { id: 'cash', label: 'Cash Payment', icon: Activity, color: 'emerald' },
                      { id: 'insurance', label: 'Insurance', icon: CheckCircle, color: 'blue' }
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => {
                          setFormData({ ...formData, eligibilityMode: formData.eligibilityMode === mode.id ? '' : mode.id });
                          setFieldErrors(p => ({ ...p, eligibilityMode: '' }));
                        }}
                        className={`p-5 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] border-2 transition-all flex flex-col items-center gap-3 sm:gap-4 group ${formData.eligibilityMode === mode.id
                          ? `bg-white border-${mode.color}-600 ring-4 ring-${mode.color}-50 shadow-xl shadow-${mode.color}-100`
                          : 'bg-slate-50 border-transparent hover:bg-slate-100 opacity-60 hover:opacity-100'
                          }`}
                      >
                        <div className={`p-3 sm:p-4 rounded-2xl transition-transform group-hover:scale-110 ${formData.eligibilityMode === mode.id ? `bg-${mode.color}-600 text-white` : 'bg-slate-200 text-slate-500'
                          }`}>
                          <mode.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <span className={`text-[11px] sm:text-sm font-black uppercase tracking-widest ${formData.eligibilityMode === mode.id ? `text-${mode.color}-700` : 'text-slate-500'
                          }`}>{mode.label}</span>
                        {formData.eligibilityMode === mode.id && <div className="p-0.5 sm:p-1 bg-indigo-600 rounded-full text-white"><Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" /></div>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 🔗 REMOTE DOCUMENT PROTOCOL */}
                <div className="relative">
                  <div className={`p-5 sm:p-8 rounded-[2rem] sm:rounded-[3rem] border-2 transition-all duration-500 ${formData.remoteIntake
                    ? 'bg-[#143E78] border-[#143E78] shadow-2xl shadow-blue-200 text-white'
                    : 'bg-white border-slate-100'
                    }`}>
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 sm:p-4 rounded-2xl ${formData.remoteIntake ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-600'}`}>
                          <Share2 className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <div>
                          <h3 className={`text-lg sm:text-xl font-black tracking-tight ${formData.remoteIntake ? 'text-white' : 'text-slate-800'}`}>Patient Document Sharing</h3>
                          <p className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-widest ${formData.remoteIntake ? 'text-blue-200' : 'text-slate-400'}`}>
                            {formData.remoteIntake ? 'Secure Access Link Will Be Generated' : 'Collect documents via secure external link'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setFormData({ ...formData, remoteIntake: !formData.remoteIntake })}
                        className={`w-full sm:w-auto px-8 py-3 rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest transition-all ${formData.remoteIntake
                          ? 'bg-white text-[#143E78] shadow-lg hover:scale-105'
                          : 'bg-slate-900 text-white hover:bg-black'
                          }`}
                      >
                        {formData.remoteIntake ? 'Cancel Document Sharing' : 'Initiate Document Sharing'}
                      </button>
                    </div>

                    {formData.remoteIntake && (
                      <div className="mt-8 pt-8 border-t border-white/10 animate-slideDown space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Generator Info */}
                          <div className="p-6 bg-white/5 rounded-3xl border border-white/10">
                            <p className="text-[10px] font-black text-blue-200 uppercase tracking-widest mb-4">Primary Recipient (Generator)</p>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <User className="w-4 h-4 text-white/60" />
                                <span className="text-sm font-bold">{validatedGenerator?.name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <MessageSquare className="w-4 h-4 text-white/60" />
                                <span className="text-sm font-mono">{validatedGenerator?.mobile || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-white/60" />
                                <span className="text-sm font-bold truncate">{validatedGenerator?.email || 'N/A'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Alternative Input */}
                          <div className="p-6 bg-white/5 rounded-3xl border border-white/10">
                            <p className="text-[10px] font-black text-blue-200 uppercase tracking-widest mb-4">Alternate Contact Information (Optional)</p>
                            <div className="space-y-4">
                              <div className="relative">
                                <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                <input
                                  type="text"
                                  placeholder="Alternate WhatsApp Number"
                                  value={formData.alternativePhone}
                                  onChange={(e) => setFormData({ ...formData, alternativePhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                  maxLength={10}
                                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl outline-none focus:border-white/40 text-sm font-bold placeholder:text-white/20"
                                />
                              </div>
                              <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                <input
                                  type="email"
                                  placeholder="Alternate Email Address"
                                  value={formData.alternativeEmail}
                                  onChange={(e) => setFormData({ ...formData, alternativeEmail: e.target.value })}
                                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl outline-none focus:border-white/40 text-sm font-bold placeholder:text-white/20"
                                />
                              </div>
                              <div className="relative">
                                <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                <select
                                  value={formData.linkExpiry}
                                  onChange={(e) => setFormData({ ...formData, linkExpiry: e.target.value })}
                                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl outline-none focus:border-white/40 text-sm font-bold appearance-none text-white/80"
                                >
                                  <option value="30" className="text-slate-900">Expires in 30 Mins</option>
                                  <option value="60" className="text-slate-900">Expires in 1 Hour</option>
                                  <option value="360" className="text-slate-900">Expires in 6 Hours</option>
                                  <option value="1440" className="text-slate-900">Expires in 24 Hours</option>
                                  <option value="10080" className="text-slate-900">Expires in 7 Days</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>


                        <div className="flex items-center gap-3 p-4 bg-blue-400/10 rounded-2xl border border-blue-400/20">
                          <Send className="w-4 h-4 text-blue-300" />
                          <p className="text-[10px] font-bold text-blue-100 uppercase tracking-wider">
                            {!remoteLead ? "Please generate a secure link first to enable document sharing." : "Secure link is ready! Choose a method below to send it."}
                          </p>
                        </div>

                        {remoteLead && remoteLead.documents?.length > 0 && (
                          <div className="space-y-4">
                            <div className="p-5 bg-emerald-500/20 border border-emerald-500/30 rounded-[24px] flex items-center justify-between animate-pulse">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                  <CheckCircle className="w-6 h-6" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-black text-white uppercase tracking-tight italic">Submission Received</h4>
                                  <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-widest">
                                    {remoteLead.documents.length} Clinical Protocols Secured
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mb-1">Time Reflect</p>
                                <p className="text-xs font-mono font-bold text-white">{new Date(remoteLead.updatedAt).toLocaleTimeString()}</p>
                              </div>
                            </div>

                            {/* Render Actual Files for Visibility */}
                            <div className="space-y-2 max-h-[140px] overflow-y-auto pr-2 custom-scrollbar">
                              {remoteLead.documents.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-white/5 p-3 rounded-2xl border border-white/10">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                                      <FileText className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <p className="text-xs font-bold text-white truncate max-w-[150px]">{doc.name || 'Clinical Document'}</p>
                                      <p className="text-[9px] uppercase font-black text-white/40 tracking-widest leading-none mt-0.5">{doc.type}</p>
                                    </div>
                                  </div>
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1.5 bg-white/10 text-white rounded-md hover:bg-white/20 transition-all"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {!remoteLead ? (
                          <button
                            onClick={initiateRemoteProtocol}
                            disabled={loading}
                            className="w-full py-4 bg-white text-[#143E78] rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50"
                          >
                            {loading ? 'Initializing Protocol...' : 'Initialize & Generate Link'}
                          </button>
                        ) : (
                          <div className="grid grid-cols-2 gap-4 animate-fadeIn">
                            <a
                              href={`https://wa.me/${formData.alternativePhone || validatedGenerator?.mobile}?text=${encodeURIComponent(`Hello ${formData.patientName}, please use this secure link to upload your medical documents: ${window.location.origin}/clinical-intake/${remoteLead._id}\n\nNote: Link expires in ${formData.linkExpiry === '30' ? '30 mins' : formData.linkExpiry === '60' ? '1 hour' : formData.linkExpiry === '1440' ? '24 hours' : formData.linkExpiry === '10080' ? '7 days' : formData.linkExpiry + ' mins'}.\n\nRegards,\nDOXEZ Clinical Team`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-emerald-900/20"
                            >
                              <MessageSquare className="w-4 h-4" /> WhatsApp Protocol
                            </a>
                            <button
                              onClick={sendEmailProtocol}
                              disabled={sendingEmail}
                              className="flex items-center justify-center gap-2 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-900/20 disabled:opacity-50"
                            >
                              <Mail className="w-4 h-4" /> {sendingEmail ? 'Dispatching...' : 'Email Protocol'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!formData.remoteIntake && (
                  <>
                    {/* 🆔 SECTION 1: IDENTITY PROOF */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black text-sm shadow-lg shadow-indigo-100 italic">ID</div>
                        <div>
                          <h3 className="font-black text-slate-800 tracking-tight leading-none mb-1">Patient Identity Proof</h3>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Upload valid government ID</label>
                        </div>
                      </div>

                      <div className="p-8 bg-slate-50/50 rounded-[2.5rem] border border-slate-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {/* Ayushman Card slotted specifically if active */}
                          {formData.eligibilityMode === 'ayushman' && (
                            <div className="space-y-4">
                              <p className="text-sm font-black text-slate-700">1.1 Ayushman Card</p>
                              <div className="border-2 border-dashed border-indigo-200 rounded-3xl p-6 flex flex-col items-center justify-center bg-white relative min-h-[180px] transition-all hover:bg-slate-50">
                                {formData.ayushmanCardImage ? (
                                  <div className="w-full h-full relative group">
                                    <img src={formData.ayushmanCardImage} className="w-full h-36 object-cover rounded-2xl shadow-xl border-4 border-white" alt="Ayushman Card" />
                                    <div className="absolute -top-3 -right-3 flex gap-2">
                                      <a
                                        href={formData.ayushmanCardImage}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2.5 bg-indigo-600 text-white rounded-full shadow-xl hover:scale-110 transition-all border-2 border-white"
                                      >
                                        <Eye className="w-4 h-4" />
                                      </a>
                                      <button
                                        onClick={() => setFormData({ ...formData, ayushmanCardImage: null })}
                                        className="p-2.5 bg-red-500 text-white rounded-full shadow-xl hover:scale-110 transition-all border-2 border-white"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <input type="file" id="ayush-up" className="hidden" onChange={(e) => handleFileUpload(Array.from(e.target.files), 'ayushman')} accept="image/*" />
                                    <label htmlFor="ayush-up" className="flex flex-col items-center cursor-pointer group text-center">
                                      <CreditCard className="w-10 h-10 text-indigo-200 mb-3 group-hover:text-indigo-400 transition-all" />
                                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-indigo-600 transition-all">Select Card Image</p>
                                    </label>
                                  </>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Standard Identification Proof */}
                          <div className="space-y-4">
                            <p className="text-sm font-black text-slate-700">{formData.eligibilityMode === 'ayushman' ? '1.2 ID Proof' : 'Patient ID Proof'}</p>
                            <div className="border-2 border-dashed border-indigo-200 rounded-3xl p-6 flex flex-col items-center justify-center bg-white relative min-h-[180px] transition-all hover:bg-slate-50">
                              {formData.patientIdProof ? (
                                <div className="w-full h-full relative group">
                                  <img src={formData.patientIdProof} className="w-full h-36 object-cover rounded-2xl shadow-xl border-4 border-white" alt="ID Proof" />
                                  <div className="absolute -top-3 -right-3 flex gap-2">
                                    <a
                                      href={formData.patientIdProof}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2.5 bg-indigo-600 text-white rounded-full shadow-xl hover:scale-110 transition-all border-2 border-white"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </a>
                                    <button
                                      onClick={() => setFormData({ ...formData, patientIdProof: null })}
                                      className="p-2.5 bg-red-500 text-white rounded-full shadow-xl hover:scale-110 transition-all border-2 border-white"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <input type="file" id="id-up" className="hidden" onChange={(e) => handleFileUpload(Array.from(e.target.files), 'id_proof')} accept="image/*" />
                                  <label htmlFor="id-up" className="flex flex-col items-center cursor-pointer group text-center">
                                    <ShieldCheck className="w-10 h-10 text-indigo-200 mb-3 group-hover:text-indigo-400 transition-all" />
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-indigo-600 transition-all"> {formData.eligibilityMode === 'ayushman' ? 'Aadhaar / Voter ID' : 'Attach Identity Proof'} </p>
                                  </label>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {formData.eligibilityMode === 'ayushman' && (
                          <p className="mt-6 text-[10px] font-black text-indigo-600 uppercase tracking-widest text-center bg-indigo-50 py-3 rounded-2xl border border-indigo-200 italic">Double-document protocol enforced for Ayushman leads</p>
                        )}
                      </div>
                    </div>

                    {/* 🏥 SECTION 2: MEDICAL INVESTIGATIONS */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white flex items-center justify-center font-black text-sm shadow-lg shadow-emerald-100 italic">MD</div>
                        <div>
                          <h3 className="font-black text-slate-800 tracking-tight leading-none mb-1">Medical Records</h3>
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Patient prescriptions & reports</label>
                        </div>
                      </div>

                      <div className="p-8 bg-slate-50/50 rounded-[2.5rem] border border-slate-100">
                        <div className="flex flex-col md:flex-row gap-8">
                          <div className="md:w-1/3">
                            <p className="text-sm font-black text-slate-700 mb-2">Upload Documents</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase leading-relaxed mb-6 italic">Securely attach patient prescriptions, test results, and medical history.</p>

                            <input type="file" multiple id="rep-up" className="hidden" onChange={(e) => handleFileUpload(Array.from(e.target.files), 'medical')} />
                            <label htmlFor="rep-up" className="w-full flex items-center justify-center gap-2 px-6 py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase shadow-xl shadow-indigo-100 cursor-pointer hover:bg-indigo-700 transition-all group">
                              <Upload className="w-4 h-4 group-hover:-translate-y-1 transition-transform" /> Upload Files ({formData.medicalReports.length}/15)
                            </label>
                          </div>

                          <div className="flex-1">
                            <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                              {formData.medicalReports.map((r, i) => (
                                <div key={i} className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-2xl shadow-sm animate-fadeIn group">
                                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <FileText className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1 overflow-hidden">
                                    <p className="text-xs font-black text-slate-700 truncate">{r.name}</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase">Verification Ready</p>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <a
                                      href={r.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2 text-slate-400 hover:text-indigo-600 transition-all opacity-0 group-hover:opacity-100"
                                      title="Review Document"
                                    >
                                      <Eye className="w-5 h-5" />
                                    </a>
                                    <button
                                      onClick={() => {
                                        const up = [...formData.medicalReports];
                                        up.splice(i, 1);
                                        setFormData({ ...formData, medicalReports: up });
                                      }}
                                      className="p-2 text-slate-400 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                                      title="Remove"
                                    >
                                      <XCircle className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                              {formData.medicalReports.length === 0 && (
                                <div className="h-full min-h-[180px] flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-3xl p-8 text-center bg-white">
                                  <ClipboardList className="w-10 h-10 text-slate-100 mb-3" />
                                  <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest italic">No documents uploaded yet</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex flex-col sm:flex-row justify-between gap-3 mt-10 sm:mt-12">
                  <button
                    onClick={() => {
                      setStep((validatedGenerator?.type === 'doctor' || validatedGenerator?.type === 'hospital') ? 3 : 2);
                    }}
                    className="order-2 sm:order-1 px-8 py-4 bg-white border-2 border-slate-200 text-slate-600 font-black rounded-xl sm:rounded-2xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" /> {(validatedGenerator?.type === 'doctor' || validatedGenerator?.type === 'hospital') ? 'Back to Strategy' : 'Back to Patient'}
                  </button>

                  <button
                    onClick={() => {
                      setFieldErrors(p => ({ ...p, eligibilityMode: '' }));
                      syncProgress(5);
                    }}
                    className="order-1 sm:order-2 px-10 py-4 bg-indigo-600 text-white font-black rounded-xl sm:rounded-2xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest"
                  >
                    Review Summary <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: REVIEW & CONFIRM */}
          {step === 5 && (
            <div className="p-5 sm:p-12 animate-fadeIn">
              <div className="flex items-center gap-4 mb-8 sm:mb-10 pb-6 border-b border-slate-50">
                <div className="p-2.5 sm:p-3 bg-indigo-50 rounded-2xl">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">Review & Confirm</h2>
                  <p className="text-[10px] sm:text-xs text-slate-400 font-black uppercase tracking-widest mt-1">Step 5: Verify all details before saving</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Summary Cards */}
                  <div className="p-5 sm:p-6 bg-slate-50 rounded-2xl sm:rounded-3xl border border-slate-100">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 sm:mb-4">Lead Source</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex justify-between items-center text-[11px] sm:text-sm">
                        <span className="text-slate-500 font-bold">Generator:</span>
                        <span className="text-slate-900 font-black truncate ml-2 text-[10px] sm:text-xs uppercase">{validatedGenerator?.name || 'DOXEZ Internal (Web)'}</span>
                      </div>
                      <div className="flex justify-between items-center text-[11px] sm:text-sm">
                        <span className="text-slate-500 font-bold">Type:</span>
                        <span className="text-indigo-600 font-black uppercase text-[10px] sm:text-xs">{validatedGenerator?.type || 'marketing'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6 bg-slate-50 rounded-2xl sm:rounded-3xl border border-slate-100">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 sm:mb-4">Patient Profile</h3>
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex justify-between items-center text-[11px] sm:text-sm">
                        <span className="text-slate-500 font-bold">Patient:</span>
                        <span className="text-slate-900 font-black truncate ml-2">{formData.patientName}</span>
                      </div>
                      <div className="flex justify-between items-center text-[11px] sm:text-sm">
                        <span className="text-slate-500 font-bold">Contact:</span>
                        <span className="text-slate-900 font-mono font-bold ml-2">{formData.patientPhone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 sm:p-6 bg-slate-50 rounded-2xl sm:rounded-3xl border border-slate-100">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 sm:mb-4">
                      {intakeCategory === 'ophe' ? 'OPHE Service Plan' : 'Treatment Preferences'}
                    </h3>
                    {intakeCategory === 'ophe' ? (
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex justify-between items-center text-[11px] sm:text-sm">
                          <span className="text-slate-500 font-bold">Service:</span>
                          <span className="text-teal-700 font-black truncate ml-2 text-[10px] sm:text-xs uppercase">
                            {formData.opheServiceDetails?.serviceName || 'No service selected'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-[11px] sm:text-sm">
                          <span className="text-slate-500 font-bold">Category:</span>
                          <span className="text-slate-900 font-black truncate ml-2 text-[10px] sm:text-xs">
                            {formData.opheServiceDetails?.categoryName || 'OPHE Care'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-[11px] sm:text-sm">
                          <span className="text-slate-500 font-bold">Plan & Duration:</span>
                          <span className="text-slate-800 font-black truncate ml-2 text-[10px] sm:text-xs">
                            {formData.opheServiceDetails?.planDuration === 'days7' ? '7 Days' :
                              formData.opheServiceDetails?.planDuration === 'days15' ? '15 Days' :
                                formData.opheServiceDetails?.planDuration === 'days30' ? '30 Days' : 'Per Day'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-[11px] sm:text-sm">
                          <span className="text-slate-500 font-bold">Price:</span>
                          <span className="text-emerald-700 font-black text-[11px] sm:text-xs">
                            ₹{(formData.opheServiceDetails?.price || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex justify-between items-center text-[11px] sm:text-sm">
                          <span className="text-slate-500 font-bold">Treatment Intent:</span>
                          <span className="text-emerald-600 font-black uppercase text-[10px] sm:text-xs">{formData.leadType === 'surgery' ? 'Surgery' : 'Lead Only'}</span>
                        </div>
                        <div className="flex justify-between items-center text-[11px] sm:text-sm">
                          <span className="text-slate-500 font-bold">Hospital:</span>
                          <span className="text-slate-900 font-black truncate ml-2 text-[11px] sm:text-xs">
                            {formData.assignedHospital
                              ? (hospitals.find(h => h._id === formData.assignedHospital)?.hospitalName || 'Target Site Selected')
                              : (validatedGenerator?.type === 'hospital' ? validatedGenerator?.name : 'Not Assigned Yet')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {(() => {
                    const compiledDocs = [];
                    if (formData.patientIdProof) compiledDocs.push({ name: 'Primary ID Proof', type: 'id_proof', url: formData.patientIdProof });
                    if (formData.ayushmanCardImage) compiledDocs.push({ name: 'Ayushman Card', type: 'ayushman_card', url: formData.ayushmanCardImage });
                    formData.medicalReports.forEach((r, i) => compiledDocs.push({ name: r.name || `Medical Report ${i + 1}`, type: 'medical_report', url: r.url }));
                    if (remoteLead?.documents) {
                      remoteLead.documents.forEach(d => compiledDocs.push(d));
                    }

                    return (
                      <div className="p-5 sm:p-6 bg-slate-50 rounded-2xl sm:rounded-3xl border border-slate-100 md:col-span-2">
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 sm:mb-4">Documents Compiled ({compiledDocs.length})</h3>
                        {compiledDocs.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {compiledDocs.map((doc, idx) => (
                              <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-white border border-slate-200 rounded-xl hover:border-indigo-300 transition-all group">
                                <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 group-hover:text-indigo-600" />
                                <span className="text-[10px] sm:text-xs font-bold text-slate-700 truncate max-w-[120px] sm:max-w-[150px]" title={doc.name}>{doc.name}</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs font-bold text-slate-400 italic">No documents attached.</p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="p-6 sm:p-8 bg-[#143E78] text-white rounded-[2rem] sm:rounded-[2.5rem] shadow-xl shadow-blue-900/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    <div className="flex flex-col items-center md:items-start text-center md:text-left">
                      <p className="text-[9px] sm:text-[10px] font-black text-blue-200 uppercase tracking-widest mb-1 sm:mb-2">Registered By</p>
                      <p className="text-xs sm:text-sm font-black">{user.firstName} {user.lastName} (CC/Admin)</p>
                    </div>
                    <div className="flex flex-col items-center md:items-start text-center md:text-left">
                      <p className="text-[9px] sm:text-[10px] font-black text-blue-200 uppercase tracking-widest mb-1 sm:mb-2">Status</p>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_#10b981]"></div>
                        <p className="text-xs sm:text-sm font-black">Ready to Save</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center md:items-start text-center md:text-left">
                      <p className="text-[9px] sm:text-[10px] font-black text-blue-200 uppercase tracking-widest mb-1 sm:mb-2">Lead ID</p>
                      <p className="text-xs sm:text-sm font-black uppercase text-blue-300">Auto-Generated on Save</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 sm:p-8 border-2 border-slate-100 rounded-[1.5rem] sm:rounded-3xl">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 sm:mb-4">Clinical Notes for Case Opening</h3>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter additional notes..."
                    className="w-full px-0 bg-transparent border-none focus:ring-0 font-bold text-slate-700 placeholder:text-slate-300 min-h-[80px] sm:min-h-[100px] resize-none text-sm sm:text-base"
                  ></textarea>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between mt-10 sm:mt-12 gap-3 sm:gap-4">
                <button
                  onClick={() => {
                    setStep(intakeCategory === 'ophe' ? 2 : 4);
                  }}
                  className="order-2 sm:order-1 px-8 py-4 bg-white border-2 border-slate-200 text-slate-600 font-black rounded-xl sm:rounded-2xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" /> {intakeCategory === 'ophe' ? 'Back to Patient' : 'Back to Upload Documents'}
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="order-1 sm:order-2 flex-1 px-10 sm:px-12 py-4 bg-emerald-500 text-white font-black rounded-xl sm:rounded-2xl shadow-xl shadow-emerald-200 hover:bg-emerald-600 transition-all flex items-center justify-center gap-3 disabled:opacity-50 text-xs sm:text-sm uppercase tracking-widest"
                >
                  {loading ? (
                    'Initiating Pipeline...'
                  ) : (
                    <>
                      Confirm & Create Lead
                      <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Info */}
        <p className="text-center text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mt-8">
          DOXEZ INTERNAL SYSTEMS v2.4 • SYSTEM TIMESTAMP: {new Date().toLocaleTimeString()}
        </p>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 500px; }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
        .animate-slideDown { animation: slideDown 0.4s cubic-bezier(0, 1, 0, 1) forwards; overflow: hidden; }
      `}</style>
    </div>
  );
};

export default LeadRegistrationCC;